// File Location: src/components/policy/PolicyVerificationForm.jsx

import React, { useState, useEffect, useRef } from "react";
import {
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  DollarSign,
  FileText,
  Calculator,
  Clock,
  Plus,
  Minus,
  Info,
  AlertCircle,
  Calendar,
  Percent,
  CreditCard,
  User,
  Building,
  MapPin,
  Phone,
  Activity,
  History,
  Save,
  Bell,
  HelpCircle,
  Trash2,
} from "lucide-react";
import {
  completePolicyVerification,
  createPolicyClarificationRequest,
  getApplicationForPolicyReview,
  savePolicyVerificationDraft,
} from "../../services/policyWriterService";
import { auth } from "../../services/firebaseConfig";
import { useAuth } from "../../context/AuthContext";

const PolicyVerificationForm = ({
  application,
  onSubmit,
  onClose,
  autoSaveData,
  onAutoSave,
  onRequestInfo,
  hasPendingClarification = false,
  clarificationDetails = null
}) => {
  const { userData } = useAuth();
  const user = { ...userData, uid: auth?.currentUser?.uid } || {};
  
  // Check if application is already approved - check multiple possible status locations
  const approvedBase = application?.status === 'approved' || 
                    application?.status === 'policy_approved' ||
                    application?.policy?.verificationStatus === 'approved' ||
                    application?.policyVerification?.status === 'approved' ||
                    application?.workflow?.policyVerification?.status === 'approved';

  // Sticky effective approval to avoid flipping back due to partial/lagging props updates
  const [approvedEffective, setApprovedEffective] = useState(false);

  // Override: If KYP was challenged and reassigned to Policy, enable editing again
  // Only while reassignment is actively pending for Policy; once completed, lock again
  const kypReassign = application?.workflow?.medicalVerification?.kypReassignment || null;
  const reassignedToPolicy = (
    kypReassign && (
      kypReassign.target === 'policy' || (kypReassign.targets || [])?.includes?.('policy')
    )
  );
  const reassignStatus = kypReassign?.status || 'pending';
  const isReassignmentActiveForPolicy = reassignedToPolicy && !['policy_completed', 'completed'].includes(reassignStatus);
  const isChallengedForPolicy = (
    application?.workflow?.medicalVerification?.kypHospitalAcknowledgement?.status === 'challenged' &&
    isReassignmentActiveForPolicy
  );

  // Final read-only flag for the form (use sticky effective approval)
  const isApproved = approvedEffective && !isChallengedForPolicy;

  // Debug log to check status
  console.log('PolicyVerificationForm - Application Status Debug:', {
    applicationStatus: application?.status,
    policyVerificationStatus: application?.policy?.verificationStatus,
    policyVerificationStatus2: application?.policyVerification?.status,
    workflowPolicyStatus: application?.workflow?.policyVerification?.status,
    approvedBase,
    approvedEffective,
    isChallengedForPolicy,
    isReadOnly: isApproved
  });

  const [loading, setLoading] = useState(false);
  const [reviewStartTime] = useState(new Date());
  const didInitRef = useRef(false);
  const appIdRef = useRef(application?.applicationId);

  // Auto-save timer
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [lastAutoSaveTs, setLastAutoSaveTs] = useState(null);

  // Validation and UI state
  const [validationErrors, setValidationErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  
  // Track if form has been saved as draft (required before approval/rejection)
  const [savedAsDraft, setSavedAsDraft] = useState(false);
  
  // State for rejection dialog
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // Form state
  const [verificationData, setVerificationData] = useState({
    status: "", // approved, rejected, pending_info
    comments: "",

    // Enhanced Policy Details
    policyDetails: {
      policyType: "", // Individual, Group, Family Floater
      policyNumber: "",
      inceptionDate: "",
      expiryDate: "",
      employeeActiveStatus: false,
      baseSumInsured: "",
      topUpSumInsured: "",
      cumulativeBonus: "",
      refillRechargeAmount: "",
      balanceSumInsured: "",
      effectiveTotalSumInsured: "",
      sumInsuredUtilizedCurrentYear: "",
    },

    // Room & ICU Eligibility
    roomEligibility: {
      roomType: "", // Private, Semi-Private, General
      roomAmountPerDay: "",
      icuAmountPerDay: "",
      roomCappingApplicable: false,
      roomCappingType: "percentage", // percentage, amount, percentage_or_amount_lower, room_type
      roomCappingLimit: "",
      roomCappingPercentage: "", // For percentage-based capping

      // ICU Capping
      icuCappingApplicable: false,
      icuCappingType: "percentage", // percentage, amount, percentage_or_amount_lower, icu_type
      icuCappingLimit: "",
      icuCappingPercentage: "", // For percentage-based capping
      icuType: "", // ICU, HDU, etc. (optional for future)
    },

    // Co-pay Structure
    coPayStructure: {
      // Co-pay Mandatory Check
      coPayMandatory: true,

      // General Co-pay
      generalCoPayApplicable: false,
      generalCoPayType: "percentage_of_admissible", // percentage_of_admissible, fixed_amount, percentage_or_amount_higher
      generalCoPayValue: "",

      // Age-based Co-pay
      parentalCoPayApplicable: false,
      parentalCoPayType: "percentage_of_admissible",
      parentalCoPayValue: "",
      parentalCoPayAgeThreshold: "", // Age above which parental co-pay applies

      // Location-based Co-pay
      locationBasedCoPayApplicable: false,
      metroCoPayType: "percentage_of_admissible",
      metroCoPayValue: "",
      nonMetroCoPayType: "percentage_of_admissible",
      nonMetroCoPayValue: "",

      // Non-Network Hospital Co-pay
      nonNetworkCoPayApplicable: false,
      nonNetworkCoPayType: "percentage_of_admissible",
      nonNetworkCoPayValue: "",

      // Voluntary/Mandatory Co-pay
      voluntaryCoPayApplicable: false,
      voluntaryCoPayType: "percentage_of_admissible",
      voluntaryCoPayValue: "",
      mandatoryCoPayApplicable: false,
      mandatoryCoPayType: "percentage_of_admissible",
      mandatoryCoPayValue: "",

      // Non-intimation Co-pay (only if intimation is mandatory)
      nonIntimationCoPayApplicable: false,
      nonIntimationCoPayType: "percentage_of_admissible",
      nonIntimationCoPayValue: "",
    },

    // Deductibles and Sublimits
    deductiblesAndSublimits: {
      deductiblesMandatory: true,
      compulsoryDeductible: "",
      ailmentProcedureSublimits: [],
      prePostNatalLimit: "",
    },

    // Intimation Details
    intimationDetails: {
      intimationDone: false,
      intimationId: "",
      intimationSource: "", // Phone, Online, App, Hospital
      intimationMandatory: true,
    },

    // Claim History
    claimHistory: {
      previousClaimsCurrentYear: [],
      totalClaimedCurrentYear: "",
    },

    // Policy Analysis
    policyAnalysis: {
      policyValid: false,
      coverageActive: false,
      beneficiaryEligible: false,
      treatmentCovered: false,
      preAuthRequired: false,
      preAuthObtained: false,
      claimLimitAvailable: false,
      waitingPeriodCompleted: false,
    },

    // Other fields
    eligibilityConfirmed: false,
    exclusionsApplied: [],
    approvedCoverageAmount: "",
    coverageBreakdown: {
      baseCoverage: "",
      cumulativeBonus: "",
      topUpCoverage: "",
      totalAvailable: "",
      utilized: "",
      balance: "",
    },
    clarificationRequired: false,
    clarificationDetails: {
      subject: "",
      message: "",
      requestedDocuments: [],
      priority: "normal",
      qnaQuestions: [],
      newQuestion: {
        question: "",
        required: false,
        answerType: "text",
        description: "",
      },
    },
    // Document Dispatch Address
    dispatchAddress: {
      dispatchType: "offline", // online | offline | both
      recipientType: "insurance", // insurance | tpa
      recipientName: "",
      contactNumber: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      pincode: "",
    },
  });

  // Auto-fill recipient name when TPA is selected
  useEffect(() => {
    const recipientType = String((verificationData.dispatchAddress || {}).recipientType || 'insurance').toLowerCase();
    if (recipientType !== 'tpa') return;

    const tpaName =
      application?.policy?.tpaName ||
      verificationData?.policyDetails?.tpaName ||
      application?.tpaName ||
      application?.tpa ||
      '';

    if (tpaName && verificationData.dispatchAddress?.recipientName !== tpaName) {
      setVerificationData((prev) => ({
        ...prev,
        dispatchAddress: {
          ...(prev.dispatchAddress || {}),
          recipientName: tpaName,
        },
      }));
    }
  }, [
    verificationData.dispatchAddress?.recipientType,
    application?.policy?.tpaName,
    verificationData?.policyDetails?.tpaName,
    application?.tpaName,
    application?.tpa,
  ]);

  // Auto-fill recipient name when Insurance Company is selected
  useEffect(() => {
    const recipientType = String((verificationData.dispatchAddress || {}).recipientType || 'insurance').toLowerCase();
    if (recipientType !== 'insurance') return;

    const insurer =
      application?.policy?.insuranceCompany ||
      verificationData?.policyDetails?.insuranceCompany ||
      application?.insuranceCompany ||
      '';

    if (insurer && verificationData.dispatchAddress?.recipientName !== insurer) {
      setVerificationData((prev) => ({
        ...prev,
        dispatchAddress: {
          ...(prev.dispatchAddress || {}),
          recipientName: insurer,
        },
      }));
    }
  }, [
    verificationData.dispatchAddress?.recipientType,
    application?.policy?.insuranceCompany,
    verificationData?.policyDetails?.insuranceCompany,
    application?.insuranceCompany,
  ]);

  // Helper function to validate policy duration (should be whole years)
  const validatePolicyDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return { valid: false, message: "" };

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end <= start) {
      return { valid: false, message: "End date must be after start date" };
    }

    // Calculate the difference in days
    const diffTime = end.getTime() - start.getTime();
    const diffDays = diffTime / (1000 * 3600 * 24);

    // Check if it's exactly whole years (365 days per year)
    const years = Math.round(diffDays / 365);
    const expectedDays = years * 365;

    if (Math.abs(diffDays - expectedDays) > 1) {
      return {
        valid: false,
        message: "Policy duration must be in whole years only (1 year, 2 years, etc.)"
      };
    }

    return {
      valid: true,
      message: years > 1 ? `Policy duration: ${years} years` : `Policy duration: ${years} year`,
      years: years
    };
  };

  // Reset init ref if application changes
  useEffect(() => {
    if (appIdRef.current !== application?.applicationId) {
      appIdRef.current = application?.applicationId;
      didInitRef.current = false;
      // Reset sticky approval when switching to a different application
      setApprovedEffective(false);
    }
  }, [application?.applicationId]);

  // Keep approval sticky once we've seen it in props for this application
  useEffect(() => {
    setApprovedEffective(prev => prev || Boolean(approvedBase));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvedBase, application?.workflow?.policyVerification?.status, application?.status]);

  // Initialize form data - priority (when not approved): autoSaveData > Firestore > localStorage > application data
  useEffect(() => {
    if (didInitRef.current) return;
    const initializeFormData = async () => {
      // If already approved, prefer Firestore; else prefer auto-save
      if (!isApproved && autoSaveData && autoSaveData.verificationData) {
        setVerificationData(autoSaveData.verificationData);
        setSavedAsDraft(true);
        didInitRef.current = true;
        console.log('Loaded verification data from autoSave (draft preferred)');
        return;
      }

      // Load from Firestore
      const firestoreDataLoaded = await loadSavedVerificationData();
      if (firestoreDataLoaded) {
        console.log('Loaded verification data from Firestore');
        didInitRef.current = true;
        return;
      }
      
      // If already approved but no Firestore data, don't load other sources
      if (isApproved) { didInitRef.current = true; return; }
      
      // Third priority: Try to load from localStorage
      try {
        const savedData = localStorage.getItem(`policyVerification_${application?.applicationId}`);
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          if (parsedData.verificationData) {
            setVerificationData(parsedData.verificationData);
            console.log('Loaded form data from localStorage');
            didInitRef.current = true;
            return;
          }
        }
      } catch (error) {
        console.warn('Failed to load from localStorage:', error);
      }
      
      // Fourth priority: Initialize with application data if no saved data
      if (application) {
        const baseSumInsured = application.policy?.coverageAmount || 0;
        const estimatedCost = application.medical?.estimatedCost || 0;

        setVerificationData(prev => ({
          ...prev,
          policyDetails: {
            ...prev.policyDetails,
            policyNumber: application.policy?.number || "",
            inceptionDate: application.policy?.startDate || "",
            expiryDate: application.policy?.endDate || "",
            baseSumInsured: baseSumInsured,
            effectiveTotalSumInsured: baseSumInsured,
            balanceSumInsured: baseSumInsured,
          },
          coverageBreakdown: {
            baseCoverage: baseSumInsured,
            cumulativeBonus: 0,
            topUpCoverage: 0,
            totalAvailable: baseSumInsured,
            utilized: 0,
            balance: baseSumInsured,
          },
          approvedCoverageAmount: Math.min(estimatedCost, baseSumInsured),
        }));
      }
    didInitRef.current = true;
    };
    
    // Call initialization function
    initializeFormData();
  }, [autoSaveData, application, isApproved]);

  // Auto-populate policy data from application when component mounts
  useEffect(() => {
    if (application?.policy) {
      const policyData = application.policy;
      setVerificationData(prev => ({
        ...prev,
        policyDetails: {
          ...prev.policyDetails,
          policyType: policyData.type || "",
          policyNumber: policyData.number || "",
          inceptionDate: policyData.startDate || "",
          expiryDate: policyData.endDate || "",
          insuranceCompany: policyData.insuranceCompany || "",
          tpaName: policyData.tpaName || "",
        }
      }));
    }
  }, [application]);

  // Auto-save functionality - save every 30 seconds when form data changes
  useEffect(() => {
    if (isApproved) return; // Don't auto-save if already approved
    
    if (onAutoSave) {
      // Clear existing timer
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }

      // Set new timer
      const timer = setTimeout(async () => {
        const saveData = {
          verificationData,
          lastModified: new Date().toISOString(),
          applicationId: application?.applicationId
        };
        
        // Save to localStorage as backup
        try {
          localStorage.setItem(
            `policyVerification_${application?.applicationId}`,
            JSON.stringify(saveData)
          );
        } catch (error) {
          console.warn('Failed to save to localStorage:', error);
        }
        
        // Save draft to Firestore; only show toast if DB write succeeds
        try {
          const res = await savePolicyVerificationDraft(application?.applicationId, user.uid, saveData.verificationData);
          if (res?.success) {
            setSavedAsDraft(true);
            setLastAutoSaveTs(new Date());
            // Optional: Call provided auto-save callback after DB save
            onAutoSave && onAutoSave(saveData);
          }
        } catch (e) {
          console.error('Auto-save draft failed:', e);
        }
      }, 10000); // Auto-save every 10 seconds

      setAutoSaveTimer(timer);

      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    }
  }, [verificationData, onAutoSave, isApproved, application?.applicationId]);

  // Manual save function for immediate save
  const handleManualSave = async () => {
    if (isApproved) {
      alert('Form is already approved and cannot be modified.');
      return;
    }
    
    const saveData = {
      verificationData,
      lastModified: new Date().toISOString(),
      applicationId: application?.applicationId
    };
    
    // Save to Firestore first; only then show success
    try {
      const res = await savePolicyVerificationDraft(application?.applicationId, user.uid, saveData.verificationData);
      if (!res?.success) throw new Error(res?.error || 'Failed to save draft');
      setSavedAsDraft(true);
      onAutoSave && onAutoSave(saveData);
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50';
      toast.textContent = 'Draft saved to cloud';
      document.body.appendChild(toast);
      setTimeout(() => { document.body.removeChild(toast); }, 1600);
    } catch (e) {
      console.error('Manual draft save failed:', e);
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-50';
      toast.textContent = 'Failed to save draft';
      document.body.appendChild(toast);
      setTimeout(() => { document.body.removeChild(toast); }, 2000);
    }
  };

  const handlePolicyDetailsChange = (field, value) => {
    if (isApproved) return; // Prevent changes if approved
    
    setVerificationData((prev) => {
      const newDetails = {
        ...prev.policyDetails,
        [field]: value,
      };

      // Validate policy duration when dates change
      if (field === 'inceptionDate' || field === 'expiryDate') {
        const validation = validatePolicyDuration(
          field === 'inceptionDate' ? value : newDetails.inceptionDate,
          field === 'expiryDate' ? value : newDetails.expiryDate
        );

        if (!validation.valid && validation.message) {
          // Show validation error temporarily
          setTimeout(() => {
            alert(validation.message);
          }, 100);
        }
      }

      // Auto-calculate effective total sum insured
      if (['baseSumInsured', 'topUpSumInsured', 'cumulativeBonus', 'refillRechargeAmount', 'sumInsuredUtilizedCurrentYear'].includes(field)) {
        const baseCoverage = parseFloat(newDetails.baseSumInsured) || 0;
        const topUpCoverage = parseFloat(newDetails.topUpSumInsured) || 0;
        const cumulativeBonus = parseFloat(newDetails.cumulativeBonus) || 0;
        const refillRecharge = parseFloat(newDetails.refillRechargeAmount) || 0;
        const utilized = parseFloat(newDetails.sumInsuredUtilizedCurrentYear) || 0;

        newDetails.effectiveTotalSumInsured = baseCoverage + topUpCoverage + cumulativeBonus + refillRecharge;
        newDetails.balanceSumInsured = newDetails.effectiveTotalSumInsured - utilized;
      }

      // Calculate coverage breakdown
      const coverageBreakdown = {
        baseCoverage: parseFloat(newDetails.baseSumInsured) || 0,
        cumulativeBonus: parseFloat(newDetails.cumulativeBonus) || 0,
        topUpCoverage: parseFloat(newDetails.topUpSumInsured) || 0,
        totalAvailable: parseFloat(newDetails.effectiveTotalSumInsured) || 0,
        utilized: parseFloat(newDetails.sumInsuredUtilizedCurrentYear) || 0,
        balance: parseFloat(newDetails.balanceSumInsured) || 0,
        effectiveTotalSumInsured: parseFloat(newDetails.effectiveTotalSumInsured) || 0,
        balanceSumInsured: parseFloat(newDetails.balanceSumInsured) || 0
      };

      // Set approved coverage amount to the balance available
      const approvedCoverageAmount = Math.min(
        application?.medical?.estimatedCost || 0,
        parseFloat(newDetails.balanceSumInsured) || 0
      );

      // Recalculate room and ICU amounts if base sum insured changes
      let updatedRoomEligibility = prev.roomEligibility;
      if (field === 'baseSumInsured') {
        const calculatedRoomAmount = calculateRoomAmount(prev.roomEligibility, newDetails.baseSumInsured);
        const calculatedIcuAmount = calculateIcuAmount(prev.roomEligibility, newDetails.baseSumInsured);
        updatedRoomEligibility = {
          ...prev.roomEligibility,
          roomAmountPerDay: calculatedRoomAmount,
          icuAmountPerDay: calculatedIcuAmount
        };
      }

      return {
        ...prev,
        policyDetails: newDetails,
        coverageBreakdown,
        approvedCoverageAmount,
        roomEligibility: updatedRoomEligibility,
      };
    });
  };

  // Calculate room amount based on capping settings
  const calculateRoomAmount = (roomEligibility, baseSumInsured) => {
    if (!roomEligibility.roomCappingApplicable) {
      return 0; // No capping, room amount is not restricted
    }

    const baseAmount = parseFloat(baseSumInsured) || 0;
    const { roomCappingType, roomCappingPercentage, roomCappingLimit } = roomEligibility;

    switch (roomCappingType) {
      case 'percentage':
        // Calculate percentage of base sum insured
        const percentage = parseFloat(roomCappingPercentage) || 0;
        return Math.round((baseAmount * percentage) / 100);

      case 'amount':
        // Return fixed amount
        return parseFloat(roomCappingLimit) || 0;

      case 'percentage_or_amount_lower':
        // Calculate both and return the lower value
        const percentageAmount = Math.round((baseAmount * (parseFloat(roomCappingPercentage) || 0)) / 100);
        const fixedAmount = parseFloat(roomCappingLimit) || 0;
        return Math.min(percentageAmount, fixedAmount);

      case 'room_type':
        // Room type based capping: do not auto-assign numeric amount without tariff; keep display by room type
        return 0;

      default:
        return 0;
    }
  };

  // Calculate ICU amount based on capping settings
  const calculateIcuAmount = (roomEligibility, baseSumInsured) => {
    if (!roomEligibility.icuCappingApplicable) {
      return 0; // No capping, ICU amount is not restricted
    }

    const baseAmount = parseFloat(baseSumInsured) || 0;
    const { icuCappingType, icuCappingPercentage, icuCappingLimit } = roomEligibility;

    switch (icuCappingType) {
      case 'percentage':
        // Calculate percentage of base sum insured
        const percentage = parseFloat(icuCappingPercentage) || 0;
        return Math.round((baseAmount * percentage) / 100);

      case 'amount':
        // Return fixed amount
        return parseFloat(icuCappingLimit) || 0;

      case 'percentage_or_amount_lower':
        // Calculate both and return the lower value
        const percentageAmount = Math.round((baseAmount * (parseFloat(icuCappingPercentage) || 0)) / 100);
        const fixedAmount = parseFloat(icuCappingLimit) || 0;
        return Math.min(percentageAmount, fixedAmount);

      case 'icu_type':
        // ICU type based capping - return predefined amounts based on ICU type
        const icuTypeAmounts = {
          'Standard ICU': 3000,
          'Cardiac ICU': 5000,
          'Neuro ICU': 6000,
          'Pediatric ICU': 4000,
          'NICU': 7000
        };
        return icuTypeAmounts[roomEligibility.icuType] || 0;

      default:
        return 0;
    }
  };

  const handleRoomEligibilityChange = (field, value) => {
    if (isApproved) return; // Prevent changes if approved
    
    setVerificationData((prev) => {
      const newRoomEligibility = {
        ...prev.roomEligibility,
        [field]: value,
      };

      // Calculate room amount when relevant fields change
      let calculatedRoomAmount = newRoomEligibility.roomAmountPerDay;
      let calculatedIcuAmount = newRoomEligibility.icuAmountPerDay;

      if (['roomCappingApplicable', 'roomCappingType', 'roomCappingPercentage', 'roomCappingLimit', 'roomType'].includes(field)) {
        calculatedRoomAmount = calculateRoomAmount(newRoomEligibility, prev.policyDetails.baseSumInsured);
        // If capping is by room type, leave amount blank to avoid misleading defaults
        newRoomEligibility.roomAmountPerDay = (newRoomEligibility.roomCappingType === 'room_type') ? '' : calculatedRoomAmount;
      }

      // Calculate ICU amount when relevant fields change
      if (['icuCappingApplicable', 'icuCappingType', 'icuCappingPercentage', 'icuCappingLimit', 'icuType'].includes(field)) {
        calculatedIcuAmount = calculateIcuAmount(newRoomEligibility, prev.policyDetails.baseSumInsured);
        newRoomEligibility.icuAmountPerDay = calculatedIcuAmount;
      }

      return {
        ...prev,
        roomEligibility: newRoomEligibility,
      };
    });
  };

  const handleCoPayChange = (field, value) => {
    if (isApproved) return; // Prevent changes if approved
    
    setVerificationData((prev) => ({
      ...prev,
      coPayStructure: {
        ...prev.coPayStructure,
        [field]: value,
      },
    }));
  };

  const handleDeductibleChange = (field, value) => {
    if (isApproved) return; // Prevent changes if approved
    
    setVerificationData((prev) => ({
      ...prev,
      deductiblesAndSublimits: {
        ...prev.deductiblesAndSublimits,
        [field]: value,
      },
    }));
  };

  const handleIntimationChange = (field, value) => {
    if (isApproved) return; // Prevent changes if approved
    
    setVerificationData((prev) => ({
      ...prev,
      intimationDetails: {
        ...prev.intimationDetails,
        [field]: value,
      },
    }));
  };

  const handleAnalysisChange = (field, value) => {
    if (isApproved) return; // Prevent changes if approved
    
    setVerificationData((prev) => ({
      ...prev,
      policyAnalysis: {
        ...prev.policyAnalysis,
        [field]: value,
      },
    }));
  };

  // Dispatch Address change handler
  const handleDispatchAddressChange = (field, value) => {
    if (isApproved) return; // Prevent changes if approved
    
    setVerificationData((prev) => ({
      ...prev,
      dispatchAddress: {
  ...(prev.dispatchAddress || {}),
        [field]: value,
      },
    }));
  };

  // Sublimit management
  const addSublimit = () => {
    if (isApproved) return; // Prevent changes if approved
    
    setVerificationData((prev) => ({
      ...prev,
      deductiblesAndSublimits: {
        ...prev.deductiblesAndSublimits,
        ailmentProcedureSublimits: [
          ...prev.deductiblesAndSublimits.ailmentProcedureSublimits,
          { ailmentProcedure: "", sublimitAmount: 0 }
        ],
      },
    }));
  };

  const removeSublimit = (index) => {
    if (isApproved) return; // Prevent changes if approved
    
    setVerificationData((prev) => ({
      ...prev,
      deductiblesAndSublimits: {
        ...prev.deductiblesAndSublimits,
        ailmentProcedureSublimits: prev.deductiblesAndSublimits.ailmentProcedureSublimits.filter((_, i) => i !== index),
      },
    }));
  };

  const updateSublimit = (index, field, value) => {
    setVerificationData((prev) => ({
      ...prev,
      deductiblesAndSublimits: {
        ...prev.deductiblesAndSublimits,
        ailmentProcedureSublimits: prev.deductiblesAndSublimits.ailmentProcedureSublimits.map((sublimit, i) =>
          i === index ? { ...sublimit, [field]: value } : sublimit
        ),
      },
    }));
  };

  // Previous claims management
  const addPreviousClaim = () => {
    if (isApproved) return; // Prevent changes if approved
    
    setVerificationData((prev) => ({
      ...prev,
      claimHistory: {
        ...prev.claimHistory,
        previousClaimsCurrentYear: [
          ...prev.claimHistory.previousClaimsCurrentYear,
          { claimNumber: "", claimDate: "", claimAmount: 0, status: "Settled" }
        ],
      },
    }));
  };

  const removePreviousClaim = (index) => {
    if (isApproved) return; // Prevent changes if approved
    
    setVerificationData((prev) => {
      const newClaims = prev.claimHistory.previousClaimsCurrentYear.filter((_, i) => i !== index);
      const totalClaimed = newClaims.reduce((sum, claim) => sum + (claim.claimAmount || 0), 0);

      return {
        ...prev,
        claimHistory: {
          previousClaimsCurrentYear: newClaims,
          totalClaimedCurrentYear: totalClaimed,
        },
      };
    });
  };

  const updatePreviousClaim = (index, field, value) => {
    setVerificationData((prev) => {
      const newClaims = prev.claimHistory.previousClaimsCurrentYear.map((claim, i) =>
        i === index ? { ...claim, [field]: value } : claim
      );

      const totalClaimed = newClaims.reduce((sum, claim) => sum + (claim.claimAmount || 0), 0);

      return {
        ...prev,
        claimHistory: {
          previousClaimsCurrentYear: newClaims,
          totalClaimedCurrentYear: totalClaimed,
        },
      };
    });
  };

  // Exclusion management
  const addExclusion = () => {
    if (isApproved) return; // Prevent changes if approved
    
    setVerificationData((prev) => ({
      ...prev,
      exclusionsApplied: [...prev.exclusionsApplied, { reason: "", amount: 0 }],
    }));
  };

  const removeExclusion = (index) => {
    if (isApproved) return; // Prevent changes if approved
    
    setVerificationData((prev) => ({
      ...prev,
      exclusionsApplied: prev.exclusionsApplied.filter((_, i) => i !== index),
    }));
  };

  const updateExclusion = (index, field, value) => {
    setVerificationData((prev) => ({
      ...prev,
      exclusionsApplied: prev.exclusionsApplied.map((exclusion, i) =>
        i === index ? { ...exclusion, [field]: value } : exclusion
      ),
    }));
  };

  // Function to load saved verification data from Firestore
  const loadSavedVerificationData = async () => {
    if (!application?.applicationId) return;

    try {
      const result = await getApplicationForPolicyReview(application.applicationId);
      if (result.success && result.application?.workflow?.policyVerification) {
        const savedVerification = result.application.workflow.policyVerification;
        
        // Check if there's saved verification data
        if (savedVerification.policyDetails || savedVerification.roomEligibility || savedVerification.coPayStructure) {
          console.log('Loading saved verification data from Firestore');
          
          // Map the saved data back to the form structure
          const mappedData = {
            status: savedVerification.status || "",
            comments: savedVerification.comments || "",
            
            // Policy Details
            policyDetails: savedVerification.policyDetails || verificationData.policyDetails,
            
            // Room Eligibility
            roomEligibility: savedVerification.roomEligibility || verificationData.roomEligibility,
            
            // Co-pay Structure
            coPayStructure: savedVerification.coPayStructure || verificationData.coPayStructure,
            
            // Deductibles and Sublimits
            deductiblesAndSublimits: savedVerification.deductiblesAndSublimits || verificationData.deductiblesAndSublimits,
            
            // Claim History
            claimHistory: savedVerification.claimHistory || verificationData.claimHistory,
            
            // Intimation Details
            intimationDetails: savedVerification.intimationDetails || verificationData.intimationDetails,
            
            // Policy Analysis
            policyAnalysis: savedVerification.policyAnalysis || verificationData.policyAnalysis,
            
            // Coverage Breakdown
            coverageBreakdown: savedVerification.coverageBreakdown || verificationData.coverageBreakdown,
            approvedCoverageAmount: savedVerification.approvedCoverageAmount || verificationData.approvedCoverageAmount,
            
            // Other fields
            eligibilityConfirmed: savedVerification.eligibilityConfirmed || verificationData.eligibilityConfirmed,
            exclusionsApplied: savedVerification.exclusionsApplied || verificationData.exclusionsApplied,
            
            // Dispatch address and clarification details
            dispatchAddress: savedVerification.dispatchAddress || verificationData.dispatchAddress,
            clarificationDetails: savedVerification.clarificationDetails || verificationData.clarificationDetails
          };
          
          setVerificationData(prev => ({
            ...prev,
            ...mappedData
          }));
          
          // If data was loaded from Firestore, mark as saved
          setSavedAsDraft(true);
          
          return true; // Indicate data was loaded
        }
      }
    } catch (error) {
      console.error('Error loading saved verification data:', error);
    }
    
    return false; // No data was loaded
  };

  // Function to check completion status of sections for UI indicators
  const getSectionCompletionStatus = () => {
    const data = verificationData;
    const status = {
      basicInfo: true,
      roomEligibility: true,
      coPayStructure: true,
      intimationDetails: true,
      policyAnalysis: true,
      coverageBreakdown: true,
  dispatchAddress: true,
  deductiblesAndSublimits: true
    };

    // Basic Policy Information
    if (!data.policyDetails.policyType || !data.policyDetails.policyNumber || 
        !data.policyDetails.inceptionDate || !data.policyDetails.expiryDate ||
        !data.policyDetails.baseSumInsured || parseFloat(data.policyDetails.baseSumInsured) <= 0) {
      status.basicInfo = false;
    }

    // Room Eligibility
    if (data.roomEligibility.roomCappingApplicable) {
      if (!data.roomEligibility.roomCappingType ||
          (data.roomEligibility.roomCappingType === 'percentage' && !data.roomEligibility.roomCappingPercentage) ||
          (data.roomEligibility.roomCappingType === 'amount' && !data.roomEligibility.roomCappingLimit) ||
          (data.roomEligibility.roomCappingType === 'room_type' && !data.roomEligibility.roomType)) {
        status.roomEligibility = false;
      }
    }

    // Co-pay Structure
    if (data.coPayStructure.coPayMandatory) {
      let hasCoPayStructure = false;
      
      if (data.coPayStructure.generalCoPayApplicable && 
          data.coPayStructure.generalCoPayValue && parseFloat(data.coPayStructure.generalCoPayValue) > 0) {
        hasCoPayStructure = true;
      }
      
      if (data.coPayStructure.parentalCoPayApplicable && 
          data.coPayStructure.parentalCoPayValue && parseFloat(data.coPayStructure.parentalCoPayValue) > 0 &&
          data.coPayStructure.parentalCoPayAgeThreshold) {
        hasCoPayStructure = true;
      }
      
      if (data.coPayStructure.locationBasedCoPayApplicable && 
          data.coPayStructure.metroCoPayValue && parseFloat(data.coPayStructure.metroCoPayValue) > 0 &&
          data.coPayStructure.nonMetroCoPayValue && parseFloat(data.coPayStructure.nonMetroCoPayValue) > 0) {
        hasCoPayStructure = true;
      }
      
      if (!hasCoPayStructure) {
        status.coPayStructure = false;
      }
    }

    // Intimation Details
    if (data.intimationDetails.intimationMandatory && !data.intimationDetails.intimationDone) {
      status.intimationDetails = false;
    }
    if (data.intimationDetails.intimationDone && 
        (!data.intimationDetails.intimationId || !data.intimationDetails.intimationSource)) {
      status.intimationDetails = false;
    }

    // Policy Analysis
    const analysisChecks = ['policyValid', 'coverageActive', 'beneficiaryEligible', 'treatmentCovered', 'claimLimitAvailable', 'waitingPeriodCompleted'];
    const checkedAnalysis = analysisChecks.filter(check => data.policyAnalysis[check] === true);
    if (checkedAnalysis.length === 0 || !data.eligibilityConfirmed) {
      status.policyAnalysis = false;
    }

    // Coverage Breakdown
    if (!data.approvedCoverageAmount || parseFloat(data.approvedCoverageAmount) <= 0) {
      status.coverageBreakdown = false;
    }

    // Deductibles & Sublimits (treat as required in completion UI)
    if (data.deductiblesAndSublimits?.deductiblesMandatory) {
      const cd = parseFloat(data.deductiblesAndSublimits.compulsoryDeductible) || 0;
      const pn = String(data.deductiblesAndSublimits.prePostNatalLimit || '').trim();
      const sublimits = Array.isArray(data.deductiblesAndSublimits.ailmentProcedureSublimits)
        ? data.deductiblesAndSublimits.ailmentProcedureSublimits
        : [];
      const hasAny = (cd > 0) || (pn.length > 0) || sublimits.some(s => String(s.ailmentProcedure || '').trim() && (parseFloat(s.sublimitAmount) || 0) > 0);
      if (!hasAny) {
        status.deductiblesAndSublimits = false;
      }
    }

    // Dispatch Address
    const da = data.dispatchAddress || {};
    const dt = String(da.dispatchType || "offline").toLowerCase();
    const requiredFields = ['recipientName', 'addressLine1', 'city', 'state', 'pincode'];
    const dtRequired = dt !== 'online';
    const missingFields = dtRequired ? requiredFields.filter(f => !String(da[f] || "").trim()) : [];
    if ((dtRequired && missingFields.length > 0) || 
        (dtRequired && da.pincode && String(da.pincode).length !== 6) 
        ) {
      status.dispatchAddress = false;
    }

    return status;
  };

  // Helper function to get field error styling
  const getFieldErrorStyle = (fieldPath) => {
    const hasError = fieldErrors[fieldPath];
    return hasError 
      ? "border-red-500 bg-red-50 focus:ring-red-500 focus:border-red-500" 
      : "border-gray-300 focus:ring-teal-500 focus:border-teal-500";
  };

  // Helper function to get field error message
  const getFieldErrorMessage = (fieldPath) => {
    return fieldErrors[fieldPath] || null;
  };

  // Helper function to clear field error when user starts typing
  const clearFieldError = (fieldPath) => {
    if (fieldErrors[fieldPath]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldPath];
        return newErrors;
      });
    }
  };
  const validateMandatoryFields = () => {
    const errors = [];
    const fieldErrorsMap = {};
    const data = verificationData;

    // 1. Basic Policy Information Validation
    if (!data.policyDetails.policyType) {
      errors.push("Policy Type is required");
      fieldErrorsMap['policyDetails.policyType'] = "Policy Type is required";
    }
    if (!data.policyDetails.policyNumber) {
      errors.push("Policy Number is required");
      fieldErrorsMap['policyDetails.policyNumber'] = "Policy Number is required";
    }
    if (!data.policyDetails.inceptionDate) {
      errors.push("Policy Inception Date is required");
      fieldErrorsMap['policyDetails.inceptionDate'] = "Policy Inception Date is required";
    }
    if (!data.policyDetails.expiryDate) {
      errors.push("Policy Expiry Date is required");
      fieldErrorsMap['policyDetails.expiryDate'] = "Policy Expiry Date is required";
    }
    if (!data.policyDetails.baseSumInsured || parseFloat(data.policyDetails.baseSumInsured) <= 0) {
      errors.push("Base Sum Insured must be greater than 0");
      fieldErrorsMap['policyDetails.baseSumInsured'] = "Base Sum Insured must be greater than 0";
    }

    // 2. Room Eligibility Validation (Mandatory Section)
    if (data.roomEligibility.roomCappingApplicable) {
      if (!data.roomEligibility.roomCappingType) {
        errors.push("Room Capping Type is required when room capping is applicable");
        fieldErrorsMap['roomEligibility.roomCappingType'] = "Room Capping Type is required when room capping is applicable";
      }
      if (data.roomEligibility.roomCappingType === 'percentage' && !data.roomEligibility.roomCappingPercentage) {
        errors.push("Room Capping Percentage is required");
        fieldErrorsMap['roomEligibility.roomCappingPercentage'] = "Room Capping Percentage is required";
      }
      if (data.roomEligibility.roomCappingType === 'amount' && !data.roomEligibility.roomCappingLimit) {
        errors.push("Room Capping Amount is required");
        fieldErrorsMap['roomEligibility.roomCappingLimit'] = "Room Capping Amount is required";
      }
      if (data.roomEligibility.roomCappingType === 'room_type' && !data.roomEligibility.roomType) {
        errors.push("Room Type is required when using room type based capping");
        fieldErrorsMap['roomEligibility.roomType'] = "Room Type is required when using room type based capping";
      }
    } else {
      // If no room capping, ensure either a defined amount or explicit no-limit selection
      const roomAmount = parseFloat(data.roomEligibility.roomAmountPerDay) || 0;
      const roomType = data.roomEligibility.roomCappingType;
      if (!(roomAmount > 0 || roomType === 'no_limit')) {
        errors.push("Room eligibility must be set: either provide Room Amount per day or select 'No limit'.");
        fieldErrorsMap['roomEligibility.roomAmountPerDay'] = "Room eligibility must be set: either provide Room Amount per day or select 'No limit'.";
      }
    }

    // ICU Eligibility validation mirroring Room (mandatory)
    if (data.roomEligibility.icuCappingApplicable) {
      if (!data.roomEligibility.icuCappingType) {
        errors.push("ICU Capping Type is required when ICU capping is applicable");
        fieldErrorsMap['roomEligibility.icuCappingType'] = "ICU Capping Type is required when ICU capping is applicable";
      }
      if (data.roomEligibility.icuCappingType === 'percentage' && !data.roomEligibility.icuCappingPercentage) {
        errors.push("ICU Capping Percentage is required");
        fieldErrorsMap['roomEligibility.icuCappingPercentage'] = "ICU Capping Percentage is required";
      }
      if (data.roomEligibility.icuCappingType === 'amount' && !data.roomEligibility.icuCappingLimit) {
        errors.push("ICU Capping Amount is required");
        fieldErrorsMap['roomEligibility.icuCappingLimit'] = "ICU Capping Amount is required";
      }
      if (data.roomEligibility.icuCappingType === 'icu_type' && !data.roomEligibility.icuType) {
        errors.push("ICU Type is required when using ICU type based capping");
        fieldErrorsMap['roomEligibility.icuType'] = "ICU Type is required when using ICU type based capping";
      }
    } else {
      const icuAmount = parseFloat(data.roomEligibility.icuAmountPerDay) || 0;
      const icuType = data.roomEligibility.icuCappingType;
      if (!(icuAmount > 0 || icuType === 'no_limit')) {
        errors.push("ICU eligibility must be set: either provide ICU Amount per day or select 'No limit'.");
        fieldErrorsMap['roomEligibility.icuAmountPerDay'] = "ICU eligibility must be set: either provide ICU Amount per day or select 'No limit'.";
      }
    }

  // 3. Co-pay Structure Validation (Mandatory Section)
    if (data.coPayStructure.coPayMandatory) {
      let hasCoPayStructure = false;
      
      if (data.coPayStructure.generalCoPayApplicable) {
        hasCoPayStructure = true;
        if (!data.coPayStructure.generalCoPayValue || parseFloat(data.coPayStructure.generalCoPayValue) <= 0) {
          errors.push("General Co-pay Value is required when general co-pay is applicable");
          fieldErrorsMap['coPayStructure.generalCoPayValue'] = "General Co-pay Value is required when general co-pay is applicable";
        }
      }
      
      if (data.coPayStructure.parentalCoPayApplicable) {
        hasCoPayStructure = true;
        if (!data.coPayStructure.parentalCoPayValue || parseFloat(data.coPayStructure.parentalCoPayValue) <= 0) {
          errors.push("Parental Co-pay Value is required when parental co-pay is applicable");
          fieldErrorsMap['coPayStructure.parentalCoPayValue'] = "Parental Co-pay Value is required when parental co-pay is applicable";
        }
        if (!data.coPayStructure.parentalCoPayAgeThreshold) {
          errors.push("Parental Co-pay Age Threshold is required");
          fieldErrorsMap['coPayStructure.parentalCoPayAgeThreshold'] = "Parental Co-pay Age Threshold is required";
        }
      }
      
      if (data.coPayStructure.locationBasedCoPayApplicable) {
        hasCoPayStructure = true;
        if (!data.coPayStructure.metroCoPayValue || parseFloat(data.coPayStructure.metroCoPayValue) <= 0) {
          errors.push("Metro Co-pay Value is required when location-based co-pay is applicable");
          fieldErrorsMap['coPayStructure.metroCoPayValue'] = "Metro Co-pay Value is required when location-based co-pay is applicable";
        }
        if (!data.coPayStructure.nonMetroCoPayValue || parseFloat(data.coPayStructure.nonMetroCoPayValue) <= 0) {
          errors.push("Non-Metro Co-pay Value is required when location-based co-pay is applicable");
          fieldErrorsMap['coPayStructure.nonMetroCoPayValue'] = "Non-Metro Co-pay Value is required when location-based co-pay is applicable";
        }
      }
      
      if (!hasCoPayStructure) {
        errors.push("Co-Pay is mandatory: please configure at least one co-pay (General/Parental/Location/Non-network/Voluntary/Mandatory/Non-intimation)");
        fieldErrorsMap['coPayStructure'] = "Co-Pay is mandatory: please configure at least one co-pay";
      }
    }

    // 4. Intimation Details Validation
    if (data.intimationDetails.intimationMandatory && !data.intimationDetails.intimationDone) {
      errors.push("Intimation must be completed when it is mandatory");
      fieldErrorsMap['intimationDetails.intimationDone'] = "Intimation must be completed when it is mandatory";
    }
    if (data.intimationDetails.intimationDone && !data.intimationDetails.intimationId) {
      errors.push("Intimation ID is required when intimation is done");
      fieldErrorsMap['intimationDetails.intimationId'] = "Intimation ID is required when intimation is done";
    }
    if (data.intimationDetails.intimationDone && !data.intimationDetails.intimationSource) {
      errors.push("Intimation Source is required when intimation is done");
      fieldErrorsMap['intimationDetails.intimationSource'] = "Intimation Source is required when intimation is done";
    }

    // 5. Policy Eligibility Analysis (Mandatory Section)
    const analysisChecks = [
      'policyValid',
      'coverageActive', 
      'beneficiaryEligible',
      'treatmentCovered',
      'claimLimitAvailable',
      'waitingPeriodCompleted'
    ];
    
    const checkedAnalysis = analysisChecks.filter(check => data.policyAnalysis[check] === true);
    if (checkedAnalysis.length === 0) {
      errors.push("Policy Eligibility Analysis is mandatory: mark at least one checkpoint as true");
      fieldErrorsMap['policyAnalysis'] = "Policy Eligibility Analysis is mandatory: mark at least one checkpoint as true";
    }

    // 6. Eligibility Confirmation
    if (!data.eligibilityConfirmed) {
      errors.push("Eligibility verification must be confirmed");
      fieldErrorsMap['eligibilityConfirmed'] = "Eligibility verification must be confirmed";
    }

    // 7. Coverage Breakdown Validation
    if (!data.approvedCoverageAmount || parseFloat(data.approvedCoverageAmount) <= 0) {
      errors.push("Approved Coverage Amount must be specified and greater than 0");
      fieldErrorsMap['approvedCoverageAmount'] = "Approved Coverage Amount must be specified and greater than 0";
    }

    // 8. Deductibles & Sublimits (Mandatory Section)
    if (data.deductiblesAndSublimits?.deductiblesMandatory) {
      const cd = parseFloat(data.deductiblesAndSublimits.compulsoryDeductible) || 0;
      const pn = String(data.deductiblesAndSublimits.prePostNatalLimit || '').trim();
      const sublimits = Array.isArray(data.deductiblesAndSublimits.ailmentProcedureSublimits)
        ? data.deductiblesAndSublimits.ailmentProcedureSublimits
        : [];

      // Validate any provided sublimits have necessary fields
      sublimits.forEach((s, idx) => {
        const amount = parseFloat(s.sublimitAmount) || 0;
        if ((s.ailmentProcedure && !amount) || (!s.ailmentProcedure && amount)) {
          errors.push(`Sublimit #${idx + 1} must include both Ailment/Procedure and a positive Amount`);
          fieldErrorsMap[`deductiblesAndSublimits.ailmentProcedureSublimits.${idx}`] = `Sublimit #${idx + 1} must include both Ailment/Procedure and a positive Amount`;
        }
      });

      const hasAny = (cd > 0) || (pn.length > 0) || sublimits.some(s => String(s.ailmentProcedure || '').trim() && (parseFloat(s.sublimitAmount) || 0) > 0);
      if (!hasAny) {
        errors.push("Deductibles & Sublimits are mandatory: enter Compulsory Deductible or Pre/Post Natal Limit or add at least one Ailment/Procedure sublimit");
        fieldErrorsMap['deductiblesAndSublimits'] = "Deductibles & Sublimits are mandatory: enter Compulsory Deductible or Pre/Post Natal Limit or add at least one Ailment/Procedure sublimit";
      }
    }

    // 9. Dispatch Address Validation (Mandatory Section)
    const da = data.dispatchAddress || {};
    const requiredDispatchFields = [
      { key: "recipientName", label: "Recipient Name" },
      { key: "addressLine1", label: "Address Line 1" },
      { key: "city", label: "City" },
      { key: "state", label: "State" },
      { key: "pincode", label: "Pincode" },
    ];
    
    const missingDispatchFields = requiredDispatchFields.filter(f => !String(da[f.key] || "").trim());
    if (missingDispatchFields.length > 0) {
      errors.push(`Dispatch address is incomplete. Missing: ${missingDispatchFields.map(m => m.label).join(", ")}`);
      missingDispatchFields.forEach(field => {
        fieldErrorsMap[`dispatchAddress.${field.key}`] = `${field.label} is required`;
      });
    }
    
    // Validate pincode format
    if (da.pincode && String(da.pincode).length !== 6) {
      errors.push("Pincode must be exactly 6 digits");
      fieldErrorsMap['dispatchAddress.pincode'] = "Pincode must be exactly 6 digits";
    }
    
    // Validate contact number format
    if (da.contactNumber && String(da.contactNumber).replace(/\D/g, "").length !== 10) {
      errors.push("Contact number must be exactly 10 digits");
      fieldErrorsMap['dispatchAddress.contactNumber'] = "Contact number must be exactly 10 digits";
    }

    return { errors, fieldErrors: fieldErrorsMap };
  };

  // Function to handle rejection with reason
  const handleRejectionSubmit = async () => {
    if (!rejectionReason.trim()) {
      alert("Please provide a rejection reason before proceeding.");
      return;
    }

    // Update verification data with rejection reason
    const updatedVerificationData = {
      ...verificationData,
      comments: rejectionReason,
      rejectionReason: rejectionReason
    };

    setVerificationData(updatedVerificationData);
    setShowRejectionDialog(false);

    // Proceed with rejection submission
    await submitVerification("rejected", updatedVerificationData);
  };

  const handleSubmit = async (status) => {
    if (isApproved) {
      alert('This application has already been approved and cannot be modified.');
      return;
    }

    // Handle rejection with dialog for reason
    if (status === "rejected") {
      setShowRejectionDialog(true);
      return;
    }

    if (status === "pending_info") {
      // Validate clarification details
      const qnaQuestions = verificationData.clarificationDetails.qnaQuestions || [];

      if (qnaQuestions.length === 0) {
        alert("Please add at least one question in the clarification section before requesting information.");
        return;
      }
    }

    // For other statuses, proceed with submission
    await submitVerification(status);
  };

  // Separate function for actual submission to avoid duplication
  const submitVerification = async (status, dataToSubmit = verificationData) => {
    // Only require comments for final decisions (approved/rejected), not for info requests
    if (!status || (!verificationData.comments && (status === "rejected" || status === "approved"))) {
      alert("Please provide comments for your decision");
      return;
    }

    // Check for pending clarifications before approval
    if (status === "approved" && hasPendingClarification) {
      alert("Cannot approve policy while there are pending clarification requests. Please resolve all clarifications first through the discussion panel.");
      return;
    }

    // Manual save before submission
    handleManualSave();

    // Comprehensive validation for approved status only
    if (status === "approved") {
      const validationResult = validateMandatoryFields();
      
      if (validationResult.errors.length > 0) {
        // Set field errors for highlighting
        setFieldErrors(validationResult.fieldErrors);
        
        // Show validation errors with field highlighting
        alert(`Please complete all mandatory fields before approval:\n\n${validationResult.errors.join('\n')}`);
        return;
      }
      
      // Clear field errors if validation passes
      setFieldErrors({});
    }

    // Handle navigation for pending_info BEFORE setting loading state


    setLoading(true);
    try {
      const submissionData = {
        ...verificationData,
        status,
        reviewStartTime,
      };

      if (status === "pending_info") {
        // User is on clarification tab - now validate and send
        const qnaQuestions = verificationData.clarificationDetails.qnaQuestions || [];

        if (qnaQuestions.length === 0) {
          setLoading(false); // Reset loading state immediately
          setValidationErrors({
            clarification: "Please add at least one question to the Q&A form before sending the clarification request"
          });
          return;
        }

        // Create Q&A message data for the discussion
        const qnaMessageData = {
          message: verificationData.clarificationDetails.message || `📋 Policy Clarification Request: ${qnaQuestions.length} question${qnaQuestions.length > 1 ? 's' : ''}`,
          qnaQuestions: qnaQuestions.map(q => ({
            ...q,
            answers: []
          })),
          priority: verificationData.clarificationDetails.priority,
        };

        // Create clarification request first (this should send as Q&A message to discussion)
        await createPolicyClarificationRequest(
          application.applicationId,
          user.uid,
          qnaMessageData
        );

        // Trigger request info callback to show discussions
        if (onRequestInfo) {
          onRequestInfo(qnaMessageData);
        }

        // Clear validation errors and reset form
        setValidationErrors(prev => ({ ...prev, clarification: null }));

        // Reset Q&A form after successful send
        setVerificationData(prev => ({
          ...prev,
          clarificationDetails: {
            ...prev.clarificationDetails,
            qnaQuestions: [],
            newQuestion: {
              question: "",
              required: false,
              answerType: "text",
              description: "",
            },
            message: "",
          },
        }));

        alert("Q&A clarification request sent successfully! You can continue the conversation in the discussion panel.");
        return; // Don't close the form, allow user to continue working
      }

      const result = await completePolicyVerification(
        application.applicationId,
        user.uid,
        submissionData
      );

      if (result.success) {
        // Clear localStorage data when successfully submitted
        try {
          localStorage.removeItem(`policyVerification_${application.applicationId}`);
        } catch (error) {
          console.warn('Failed to clear localStorage:', error);
        }
        
        alert(`Policy verification ${status} successfully!`);
        onSubmit && onSubmit(result);
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error("Error submitting verification:", error);
      alert("Failed to submit verification. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return "₹0";
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const renderPolicyDetails = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900">
              Policy Details Verification
            </h4>
            <p className="text-sm text-blue-700 mt-1">
              Verify and complete all policy details before proceeding with eligibility analysis.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Policy Information */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900">Basic Policy Information</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Policy Type *
            </label>
            <select
              value={verificationData.policyDetails.policyType}
              onChange={(e) => {
                if (!isApproved) {
                  handlePolicyDetailsChange("policyType", e.target.value);
                  clearFieldError("policyDetails.policyType");
                }
              }}
              disabled={isApproved}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 ${
                isApproved ? 'bg-gray-100 cursor-not-allowed text-gray-600 border-gray-300' : getFieldErrorStyle("policyDetails.policyType")
              }`}
            >
              <option value="">Select Policy Type</option>
              <option value="Individual">Individual</option>
              <option value="Group">Group</option>
              <option value="Family Floater">Family Floater</option>
            </select>
            {getFieldErrorMessage("policyDetails.policyType") && (
              <div className="mt-1 flex items-center text-red-600 text-sm">
                <AlertCircle className="h-4 w-4 mr-1" />
                {getFieldErrorMessage("policyDetails.policyType")}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Policy Number
            </label>
            {(() => {
              const effectivePolicyNumber =
                verificationData.policyDetails.policyNumber ||
                application?.policy?.number ||
                application?.policyNumber || '';
              return (
                <div className="flex items-center justify-between w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
              <span className="font-mono text-gray-900 truncate">
                {effectivePolicyNumber || '—'}
              </span>
              <button
                type="button"
                onClick={() => {
                  try {
                    navigator.clipboard.writeText(String(effectivePolicyNumber || ''));
                    const t = document.createElement('div');
                    t.className = 'fixed top-4 right-4 bg-teal-600 text-white px-3 py-1 rounded shadow z-50 text-sm';
                    t.textContent = 'Copied policy number';
                    document.body.appendChild(t);
                    setTimeout(() => { document.body.removeChild(t); }, 1200);
                  } catch (_) {}
                }}
                className="text-xs text-teal-700 hover:text-teal-900"
              >
                Copy
              </button>
            </div>
              );
            })()}
            <p className="text-xs text-gray-500 mt-1">Locked for consistency. Edit from Policy Snapshot if required.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Policy Inception Date *
              </label>
              <input
                type="date"
                value={verificationData.policyDetails.inceptionDate}
                onChange={(e) => {
                  if (!isApproved) {
                    handlePolicyDetailsChange("inceptionDate", e.target.value);
                    clearFieldError("policyDetails.inceptionDate");
                  }
                }}
                disabled={isApproved}
                readOnly={isApproved}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 ${
                  isApproved ? 'bg-gray-100 cursor-not-allowed text-gray-600 border-gray-300' : getFieldErrorStyle("policyDetails.inceptionDate")
                }`}
              />
              {getFieldErrorMessage("policyDetails.inceptionDate") && (
                <div className="mt-1 flex items-center text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {getFieldErrorMessage("policyDetails.inceptionDate")}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Policy Expiry Date *
              </label>
              <input
                type="date"
                value={verificationData.policyDetails.expiryDate}
                onChange={(e) => {
                  if (!isApproved) {
                    handlePolicyDetailsChange("expiryDate", e.target.value);
                    clearFieldError("policyDetails.expiryDate");
                  }
                }}
                disabled={isApproved}
                readOnly={isApproved}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 ${
                  isApproved ? 'bg-gray-100 cursor-not-allowed text-gray-600 border-gray-300' : getFieldErrorStyle("policyDetails.expiryDate")
                }`}
              />
              {getFieldErrorMessage("policyDetails.expiryDate") && (
                <div className="mt-1 flex items-center text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {getFieldErrorMessage("policyDetails.expiryDate")}
                </div>
              )}
            </div>
          </div>

          {/* Policy Duration Validation Display */}
          {verificationData.policyDetails.inceptionDate && verificationData.policyDetails.expiryDate && (
            <div className="mt-3">
              {(() => {
                const validation = validatePolicyDuration(
                  verificationData.policyDetails.inceptionDate,
                  verificationData.policyDetails.expiryDate
                );
                return validation.valid ? (
                  <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium text-green-800">{validation.message}</span>
                  </div>
                ) : validation.message ? (
                  <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <XCircle className="h-5 w-5 text-red-600" />
                    <span className="text-sm font-medium text-red-800">{validation.message}</span>
                  </div>
                ) : null;
              })()}
            </div>
          )}

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="employeeActiveStatus"
              checked={verificationData.policyDetails.employeeActiveStatus}
              onChange={(e) => !isApproved && handlePolicyDetailsChange("employeeActiveStatus", e.target.checked)}
              disabled={isApproved}
              className={`h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded ${
                isApproved ? 'cursor-not-allowed' : ''
              }`}
            />
            <label htmlFor="employeeActiveStatus" className={`text-sm font-medium ${
              isApproved ? 'text-gray-500' : 'text-gray-700'
            }`}>
              Employee/Insured Active Status
            </label>
          </div>
        </div>

        {/* Sum Insured Details */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900">Sum Insured Details</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Base Sum Insured (₹)
            </label>
            <input
              type="number"
              min="0"
              value={verificationData.policyDetails.baseSumInsured}
              onChange={(e) => {
                if (!isApproved) {
                  handlePolicyDetailsChange("baseSumInsured", e.target.value);
                  clearFieldError("policyDetails.baseSumInsured");
                }
              }}
              disabled={isApproved}
              readOnly={isApproved}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                isApproved ? 'bg-gray-100 cursor-not-allowed text-gray-600 border-gray-300' : getFieldErrorStyle("policyDetails.baseSumInsured")
              }`}
              placeholder="0"
            />
            {getFieldErrorMessage("policyDetails.baseSumInsured") && (
              <div className="mt-1 flex items-center text-red-600 text-sm">
                <AlertCircle className="h-4 w-4 mr-1" />
                {getFieldErrorMessage("policyDetails.baseSumInsured")}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Top-up Sum Insured (₹)
            </label>
            <input
              type="number"
              min="0"
              value={verificationData.policyDetails.topUpSumInsured}
              onChange={(e) => !isApproved && handlePolicyDetailsChange("topUpSumInsured", e.target.value)}
              disabled={isApproved}
              readOnly={isApproved}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                isApproved ? 'bg-gray-100 cursor-not-allowed text-gray-600' : ''
              }`}
              placeholder="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cumulative Bonus (₹)
            </label>
            <input
              type="number"
              min="0"
              value={verificationData.policyDetails.cumulativeBonus}
              onChange={(e) => !isApproved && handlePolicyDetailsChange("cumulativeBonus", e.target.value)}
              disabled={isApproved}
              readOnly={isApproved}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                isApproved ? 'bg-gray-100 cursor-not-allowed text-gray-600' : ''
              }`}
              placeholder="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Refill/Recharge Amount (₹)
            </label>
            <input
              type="number"
              min="0"
              value={verificationData.policyDetails.refillRechargeAmount}
              onChange={(e) => !isApproved && handlePolicyDetailsChange("refillRechargeAmount", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              placeholder="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sum Insured Utilized (Current Year) (₹)
            </label>
            <input
              type="number"
              min="0"
              value={verificationData.policyDetails.sumInsuredUtilizedCurrentYear}
              onChange={(e) => !isApproved && handlePolicyDetailsChange("sumInsuredUtilizedCurrentYear", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              placeholder="0"
            />
          </div>

          {/* Calculated Fields */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Effective Total Sum Insured:</span>
              <span className="text-sm font-semibold text-gray-900">
                {formatAmount(verificationData.policyDetails.effectiveTotalSumInsured)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Balance Sum Insured:</span>
              <span className="text-sm font-semibold text-teal-600">
                {formatAmount(verificationData.policyDetails.balanceSumInsured)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRoomAndCoPay = () => (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Building className="h-5 w-5 text-green-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-green-900">
              Room Eligibility & Co-pay Structure
            </h4>
            <p className="text-sm text-green-700 mt-1">
              Define room eligibility and co-payment structure based on policy terms.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Room Eligibility */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900">Room Eligibility</h4>



          {/* Room Capping Checkbox */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center space-x-3 mb-3">
              <input
                type="checkbox"
                id="roomCappingApplicable"
                checked={verificationData.roomEligibility.roomCappingApplicable}
                onChange={(e) => !isApproved && handleRoomEligibilityChange("roomCappingApplicable", e.target.checked)}
                className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <label htmlFor="roomCappingApplicable" className="text-sm font-medium text-blue-900">
                Room Capping Applicable
              </label>
            </div>

            {verificationData.roomEligibility.roomCappingApplicable && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Room Capping Type
                  </label>
                  <select
                    value={verificationData.roomEligibility.roomCappingType}
                    onChange={(e) => {
                      if (!isApproved) {
                        handleRoomEligibilityChange("roomCappingType", e.target.value);
                        clearFieldError("roomEligibility.roomCappingType");
                      }
                    }}
                    disabled={isApproved}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 ${
                      isApproved ? 'bg-gray-100 cursor-not-allowed text-gray-600 border-gray-300' : `bg-white ${getFieldErrorStyle("roomEligibility.roomCappingType")}`
                    }`}
                  >
                    <option value="percentage">Percentage of Base Sum Insured (%)</option>
                    <option value="amount">Fixed Amount (₹)</option>
                    <option value="percentage_or_amount_lower">Percentage or Amount (Whichever is Lower)</option>
                    <option value="room_type">Capped by Room Type</option>
                    <option value="no_limit">No limit</option>

                  </select>
                  {getFieldErrorMessage("roomEligibility.roomCappingType") && (
                    <div className="mt-1 flex items-center text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {getFieldErrorMessage("roomEligibility.roomCappingType")}
                    </div>
                  )}
                </div>

                {verificationData.roomEligibility.roomCappingType === 'percentage' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Room Capping Percentage (% of Base Sum Insured)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={verificationData.roomEligibility.roomCappingPercentage}
                      onChange={(e) => {
                        if (!isApproved) {
                          handleRoomEligibilityChange("roomCappingPercentage", e.target.value);
                          clearFieldError("roomEligibility.roomCappingPercentage");
                        }
                      }}
                      disabled={isApproved}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                        isApproved ? 'bg-gray-100 cursor-not-allowed text-gray-600 border-gray-300' : getFieldErrorStyle("roomEligibility.roomCappingPercentage")
                      }`}
                      placeholder=""
                    />
                    {getFieldErrorMessage("roomEligibility.roomCappingPercentage") && (
                      <div className="mt-1 flex items-center text-red-600 text-sm">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {getFieldErrorMessage("roomEligibility.roomCappingPercentage")}
                      </div>
                    )}
                  </div>
                )}

                {verificationData.roomEligibility.roomCappingType === 'amount' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Room Capping Amount (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={verificationData.roomEligibility.roomCappingLimit}
                      onChange={(e) => handleRoomEligibilityChange("roomCappingLimit", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder="0"
                    />
                  </div>
                )}

                {verificationData.roomEligibility.roomCappingType === 'percentage_or_amount_lower' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Percentage (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={verificationData.roomEligibility.roomCappingPercentage}
                        onChange={(e) => handleRoomEligibilityChange("roomCappingPercentage", Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Amount (₹)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={verificationData.roomEligibility.roomCappingLimit}
                        onChange={(e) => handleRoomEligibilityChange("roomCappingLimit", Math.max(0, parseFloat(e.target.value) || 0))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="0"
                      />
                    </div>
                  </div>
                )}

                {verificationData.roomEligibility.roomCappingType === 'room_type' && (
                  <div className="text-sm text-gray-600 p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium">Room Type Based Capping:</p>
                    <p>Capping will be applied based on the selected room type and its standard rates.</p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Room Type
                      </label>
                      <select
                        value={verificationData.roomEligibility.roomType}
                        onChange={(e) => handleRoomEligibilityChange("roomType", e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                      >
                        <option value="">Select Room Type</option>
                        <option value="General">General Ward</option>
                        <option value="Semi-Private">Semi-Private</option>
                        <option value="Private">Private Room</option>
                        <option value="Deluxe">Deluxe Room</option>
                        <option value="Suite">Suite</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Room Amount Per Day (₹)
              {verificationData.roomEligibility.roomCappingApplicable && (
                <span className="text-xs text-blue-600 font-medium ml-2">(Auto-calculated)</span>
              )}
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                readOnly={verificationData.roomEligibility.roomCappingApplicable}
                value={verificationData.roomEligibility.roomAmountPerDay}
                onChange={(e) => {
                  if (!verificationData.roomEligibility.roomCappingApplicable && !isApproved) {
                    handleRoomEligibilityChange("roomAmountPerDay", Math.max(0, parseFloat(e.target.value) || 0));
                    clearFieldError("roomEligibility.roomAmountPerDay");
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                  verificationData.roomEligibility.roomCappingApplicable 
                    ? 'bg-blue-50 text-blue-900 cursor-not-allowed border-blue-300' 
                    : getFieldErrorStyle("roomEligibility.roomAmountPerDay")
                }`}
                placeholder="0"
              />
              {verificationData.roomEligibility.roomCappingApplicable && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <div className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                    Calculated
                  </div>
                </div>
              )}
            </div>
            {getFieldErrorMessage("roomEligibility.roomAmountPerDay") && (
              <div className="mt-1 flex items-center text-red-600 text-sm">
                <AlertCircle className="h-4 w-4 mr-1" />
                {getFieldErrorMessage("roomEligibility.roomAmountPerDay")}
              </div>
            )}
            {verificationData.roomEligibility.roomCappingApplicable && (
              <p className="text-xs text-gray-600 mt-1">
                Amount calculated based on {verificationData.roomEligibility.roomCappingType === 'percentage' ? 'percentage of base sum insured' :
                  verificationData.roomEligibility.roomCappingType === 'amount' ? 'fixed amount limit' :
                    verificationData.roomEligibility.roomCappingType === 'percentage_or_amount_lower' ? 'lower of percentage or fixed amount' :
                      verificationData.roomEligibility.roomCappingType === 'room_type' ? 'selected room type' : 'room capping settings'}
              </p>
            )}
          </div>

          {/* Room Calculation Breakdown */}
          {verificationData.roomEligibility.roomCappingApplicable && verificationData.policyDetails.baseSumInsured && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
              <h5 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2H6zm1 2a1 1 0 000 2h6a1 1 0 100-2H7zm6 5a1 1 0 011 1v3a1 1 0 11-2 0v-1H8v1a1 1 0 11-2 0v-3a1 1 0 011-1h6z" clipRule="evenodd" />
                </svg>
                Room Amount Calculation Breakdown
              </h5>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Base Sum Insured:</span>
                  <span className="font-medium text-gray-900">₹{parseInt(verificationData.policyDetails.baseSumInsured || 0).toLocaleString('en-IN')}</span>
                </div>

                {verificationData.roomEligibility.roomCappingType === 'percentage' && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Room Capping Percentage:</span>
                      <span className="font-medium text-blue-700">{verificationData.roomEligibility.roomCappingPercentage}%</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                      <span className="text-gray-700 font-medium">Calculated Room Amount:</span>
                      <span className="font-semibold text-blue-900">
                        ₹{parseInt(verificationData.roomEligibility.roomAmountPerDay || 0).toLocaleString('en-IN')} per day
                      </span>
                    </div>
                    <div className="text-xs text-gray-600">
                      Formula: (₹{parseInt(verificationData.policyDetails.baseSumInsured || 0).toLocaleString('en-IN')} × {verificationData.roomEligibility.roomCappingPercentage}%) ÷ 100 = ₹{parseInt(verificationData.roomEligibility.roomAmountPerDay || 0).toLocaleString('en-IN')}
                    </div>
                  </>
                )}

                {verificationData.roomEligibility.roomCappingType === 'amount' && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Fixed Capping Amount:</span>
                      <span className="font-medium text-blue-700">₹{parseInt(verificationData.roomEligibility.roomCappingLimit || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                      <span className="text-gray-700 font-medium">Applied Room Amount:</span>
                      <span className="font-semibold text-blue-900">
                        ₹{parseInt(verificationData.roomEligibility.roomAmountPerDay || 0).toLocaleString('en-IN')} per day
                      </span>
                    </div>
                  </>
                )}

                {verificationData.roomEligibility.roomCappingType === 'percentage_or_amount_lower' && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Percentage ({verificationData.roomEligibility.roomCappingPercentage}%):</span>
                      <span className="font-medium text-blue-700">
                        ₹{Math.round((parseInt(verificationData.policyDetails.baseSumInsured || 0) * (parseFloat(verificationData.roomEligibility.roomCappingPercentage) || 0)) / 100).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Fixed Amount:</span>
                      <span className="font-medium text-blue-700">₹{parseInt(verificationData.roomEligibility.roomCappingLimit || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                      <span className="text-gray-700 font-medium">Applied (Lower Value):</span>
                      <span className="font-semibold text-blue-900">
                        ₹{parseInt(verificationData.roomEligibility.roomAmountPerDay || 0).toLocaleString('en-IN')} per day
                      </span>
                    </div>
                  </>
                )}

                {verificationData.roomEligibility.roomCappingType === 'room_type' && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Selected Room Type:</span>
                      <span className="font-medium text-blue-700">{verificationData.roomEligibility.roomType}</span>
                    </div>
                    {parseFloat(verificationData.roomEligibility.roomAmountPerDay) > 0 ? (
                      <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                        <span className="text-gray-700 font-medium">Standard Room Rate:</span>
                        <span className="font-semibold text-blue-900">
                          ₹{parseInt(verificationData.roomEligibility.roomAmountPerDay || 0).toLocaleString('en-IN')} per day
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                        <span className="text-gray-700 font-medium">Eligibility Display:</span>
                        <span className="font-semibold text-blue-900">{verificationData.roomEligibility.roomType || 'As per room type'}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* ICU Capping Section */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mt-4">
            <div className="flex items-center space-x-3 mb-3">
              <input
                type="checkbox"
                id="icuCappingApplicable"
                checked={verificationData.roomEligibility.icuCappingApplicable}
                onChange={(e) => handleRoomEligibilityChange("icuCappingApplicable", e.target.checked)}
                className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <label htmlFor="icuCappingApplicable" className="text-sm font-medium text-blue-900">
                ICU Capping Applicable
              </label>
            </div>

            {verificationData.roomEligibility.icuCappingApplicable && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ICU Capping Type
                  </label>
                  <select
                    value={verificationData.roomEligibility.icuCappingType}
                    onChange={(e) => handleRoomEligibilityChange("icuCappingType", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                  >
                    <option value="percentage">Percentage of Base Sum Insured (%)</option>
                    <option value="amount">Fixed Amount (₹)</option>
                    <option value="percentage_or_amount_lower">Percentage or Amount (Whichever is Lower)</option>
                    <option value="icu_type">Capped by ICU Type</option>
                    <option value="no_limit">No limit</option>

                  </select>
                </div>

                {verificationData.roomEligibility.icuCappingType === 'percentage' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ICU Capping Percentage (% of Base Sum Insured)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={verificationData.roomEligibility.icuCappingPercentage}
                      onChange={(e) => handleRoomEligibilityChange("icuCappingPercentage", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder=""
                    />
                  </div>
                )}

                {verificationData.roomEligibility.icuCappingType === 'amount' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ICU Capping Amount (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={verificationData.roomEligibility.icuCappingLimit}
                      onChange={(e) => handleRoomEligibilityChange("icuCappingLimit", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder="0"
                    />
                  </div>
                )}

                {verificationData.roomEligibility.icuCappingType === 'percentage_or_amount_lower' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Percentage (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={verificationData.roomEligibility.icuCappingPercentage}
                        onChange={(e) => handleRoomEligibilityChange("icuCappingPercentage", Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Amount (₹)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={verificationData.roomEligibility.icuCappingLimit}
                        onChange={(e) => handleRoomEligibilityChange("icuCappingLimit", Math.max(0, parseFloat(e.target.value) || 0))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="0"
                      />
                    </div>
                  </div>
                )}

                {verificationData.roomEligibility.icuCappingType === 'icu_type' && (
                  <div className="text-sm text-gray-600 p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium">ICU Type Based Capping:</p>
                    <p>Capping will be applied based on the selected ICU type and its standard rates.</p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ICU Type
                      </label>
                      <select
                        value={verificationData.roomEligibility.icuType}
                        onChange={(e) => handleRoomEligibilityChange("icuType", e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                      >
                        <option value="">Select ICU Type</option>
                        <option value="Standard ICU">Standard ICU</option>
                        <option value="Cardiac ICU">Cardiac ICU</option>
                        <option value="Neuro ICU">Neuro ICU</option>
                        <option value="Pediatric ICU">Pediatric ICU</option>
                        <option value="NICU">NICU</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ICU Amount Per Day (₹)
                {verificationData.roomEligibility.icuCappingApplicable && (
                  <span className="text-xs text-blue-600 font-medium ml-2">(Auto-calculated)</span>
                )}
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  readOnly={verificationData.roomEligibility.icuCappingApplicable}
                  value={verificationData.roomEligibility.icuAmountPerDay}
                  onChange={(e) => !verificationData.roomEligibility.icuCappingApplicable && handleRoomEligibilityChange("icuAmountPerDay", Math.max(0, parseFloat(e.target.value) || 0))}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${verificationData.roomEligibility.icuCappingApplicable ? 'bg-blue-50 text-blue-900 cursor-not-allowed' : ''
                    }`}
                  placeholder="0"
                />
                {verificationData.roomEligibility.icuCappingApplicable && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <div className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                      Calculated
                    </div>
                  </div>
                )}
              </div>
              {verificationData.roomEligibility.icuCappingApplicable && (
                <p className="text-xs text-gray-600 mt-1">
                  Amount calculated based on {verificationData.roomEligibility.icuCappingType === 'percentage' ? 'percentage of base sum insured' :
                    verificationData.roomEligibility.icuCappingType === 'amount' ? 'fixed amount limit' :
                      verificationData.roomEligibility.icuCappingType === 'percentage_or_amount_lower' ? 'lower of percentage or fixed amount' :
                        verificationData.roomEligibility.icuCappingType === 'icu_type' ? 'selected ICU type' : 'ICU capping settings'}
                </p>
              )}
            </div>

            {/* ICU Calculation Breakdown */}
            {verificationData.roomEligibility.icuCappingApplicable && verificationData.policyDetails.baseSumInsured && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mt-4">
                <h5 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2H6zm1 2a1 1 0 000 2h6a1 1 0 100-2H7zm6 5a1 1 0 011 1v3a1 1 0 11-2 0v-1H8v1a1 1 0 11-2 0v-3a1 1 0 011-1h6z" clipRule="evenodd" />
                  </svg>
                  ICU Amount Calculation Breakdown
                </h5>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Base Sum Insured:</span>
                    <span className="font-medium text-gray-900">₹{parseInt(verificationData.policyDetails.baseSumInsured || 0).toLocaleString('en-IN')}</span>
                  </div>
                  {verificationData.roomEligibility.icuCappingType === 'percentage' && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">ICU Capping Percentage:</span>
                        <span className="font-medium text-blue-700">{verificationData.roomEligibility.icuCappingPercentage}%</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                        <span className="text-gray-700 font-medium">Calculated ICU Amount:</span>
                        <span className="font-semibold text-blue-900">
                          ₹{parseInt(verificationData.roomEligibility.icuAmountPerDay || 0).toLocaleString('en-IN')} per day
                        </span>
                      </div>
                      <div className="text-xs text-gray-600">
                        Formula: (₹{parseInt(verificationData.policyDetails.baseSumInsured || 0).toLocaleString('en-IN')} × {verificationData.roomEligibility.icuCappingPercentage}%) ÷ 100 = ₹{parseInt(verificationData.roomEligibility.icuAmountPerDay || 0).toLocaleString('en-IN')}
                      </div>
                    </>
                  )}
                  {verificationData.roomEligibility.icuCappingType === 'amount' && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Fixed Capping Amount:</span>
                        <span className="font-medium text-blue-700">₹{parseInt(verificationData.roomEligibility.icuCappingLimit || 0).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                        <span className="text-gray-700 font-medium">Applied ICU Amount:</span>
                        <span className="font-semibold text-blue-900">
                          ₹{parseInt(verificationData.roomEligibility.icuAmountPerDay || 0).toLocaleString('en-IN')} per day
                        </span>
                      </div>
                    </>
                  )}
                  {verificationData.roomEligibility.icuCappingType === 'percentage_or_amount_lower' && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Percentage ({verificationData.roomEligibility.icuCappingPercentage}%):</span>
                        <span className="font-medium text-blue-700">
                          ₹{Math.round((parseInt(verificationData.policyDetails.baseSumInsured || 0) * (parseFloat(verificationData.roomEligibility.icuCappingPercentage) || 0)) / 100).toLocaleString('en-IN')}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Fixed Amount:</span>
                        <span className="font-medium text-blue-700">₹{parseInt(verificationData.roomEligibility.icuCappingLimit || 0).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                        <span className="text-gray-700 font-medium">Applied (Lower Value):</span>
                        <span className="font-semibold text-blue-900">
                          ₹{parseInt(verificationData.roomEligibility.icuAmountPerDay || 0).toLocaleString('en-IN')} per day
                        </span>
                      </div>
                    </>
                  )}
                  {verificationData.roomEligibility.icuCappingType === 'icu_type' && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Selected ICU Type:</span>
                        <span className="font-medium text-blue-700">{verificationData.roomEligibility.icuType}</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                        <span className="text-gray-700 font-medium">Standard ICU Rate:</span>
                        <span className="font-semibold text-blue-900">
                          ₹{parseInt(verificationData.roomEligibility.icuAmountPerDay || 0).toLocaleString('en-IN')} per day
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Co-pay Structure */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900">Co-pay Structure</h4>

          {/* Co-pay Mandatory Check */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-start space-x-3">
              <div className="flex items-center space-x-3">
                <input
                  type="radio"
                  id="coPayMandatoryYes"
                  name="coPayMandatory"
                  checked={verificationData.coPayStructure.coPayMandatory === true}
                  onChange={() => handleCoPayChange("coPayMandatory", true)}
                  className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300"
                />
                <label htmlFor="coPayMandatoryYes" className="text-sm font-medium text-blue-900">
                  Yes, Co-pay is Mandatory
                </label>
              </div>
              <div className="flex items-center space-x-3 ml-6">
                <input
                  type="radio"
                  id="coPayMandatoryNo"
                  name="coPayMandatory"
                  checked={verificationData.coPayStructure.coPayMandatory === false}
                  onChange={() => handleCoPayChange("coPayMandatory", false)}
                  className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300"
                />
                <label htmlFor="coPayMandatoryNo" className="text-sm font-medium text-blue-900">
                  No Co-pay Required
                </label>
              </div>
            </div>
            <p className="text-sm text-blue-700 mt-2">
              {verificationData.coPayStructure.coPayMandatory ?
                "Configure the applicable co-pay structures below:" :
                "No co-pay structure will be applied to this policy."
              }
            </p>
          </div>

          {/* Show co-pay options only if mandatory */}
          {verificationData.coPayStructure.coPayMandatory && (
            <>
              {/* General Co-pay */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center space-x-3 mb-3">
                  <input
                    type="checkbox"
                    id="generalCoPayApplicable"
                    checked={verificationData.coPayStructure.generalCoPayApplicable}
                    onChange={(e) => handleCoPayChange("generalCoPayApplicable", e.target.checked)}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <label htmlFor="generalCoPayApplicable" className="text-sm font-medium text-gray-900">
                    General Co-pay Applicable
                  </label>
                </div>

                {verificationData.coPayStructure.generalCoPayApplicable && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Co-pay Type
                      </label>
                      <select
                        value={verificationData.coPayStructure.generalCoPayType}
                        onChange={(e) => handleCoPayChange("generalCoPayType", e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                      >
                        <option value="percentage_of_admissible">% of Claim Admissible Amount</option>
                        <option value="fixed_amount">Fixed Amount on Admissible Amount</option>
                        <option value="percentage_or_amount_higher">% or Amount (Whichever is Higher)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Co-pay Value ({verificationData.coPayStructure.generalCoPayType.includes('percentage') ? '%' : '₹'})
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={verificationData.coPayStructure.generalCoPayValue}
                        onChange={(e) => {
                          if (!isApproved) {
                            handleCoPayChange("generalCoPayValue", Math.max(0, parseFloat(e.target.value) || 0));
                            clearFieldError("coPayStructure.generalCoPayValue");
                          }
                        }}
                        disabled={isApproved}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                          isApproved ? 'bg-gray-100 cursor-not-allowed text-gray-600 border-gray-300' : getFieldErrorStyle("coPayStructure.generalCoPayValue")
                        }`}
                        placeholder="0"
                      />
                      {getFieldErrorMessage("coPayStructure.generalCoPayValue") && (
                        <div className="mt-1 flex items-center text-red-600 text-sm">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {getFieldErrorMessage("coPayStructure.generalCoPayValue")}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Parental Co-pay */}
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <div className="flex items-center space-x-3 mb-3">
                  <input
                    type="checkbox"
                    id="parentalCoPayApplicable"
                    checked={verificationData.coPayStructure.parentalCoPayApplicable}
                    onChange={(e) => handleCoPayChange("parentalCoPayApplicable", e.target.checked)}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <label htmlFor="parentalCoPayApplicable" className="text-sm font-medium text-orange-900">
                    Parental Co-pay (Age-based)
                  </label>
                </div>

                {verificationData.coPayStructure.parentalCoPayApplicable && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Age Threshold for Parental Co-pay
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={verificationData.coPayStructure.parentalCoPayAgeThreshold}
                        onChange={(e) => handleCoPayChange("parentalCoPayAgeThreshold", Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="60"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Parental Co-pay Type
                        </label>
                        <select
                          value={verificationData.coPayStructure.parentalCoPayType}
                          onChange={(e) => handleCoPayChange("parentalCoPayType", e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                        >
                          <option value="percentage_of_admissible">% of Claim Admissible Amount</option>
                          <option value="fixed_amount">Fixed Amount on Admissible Amount</option>
                          <option value="percentage_or_amount_higher">% or Amount (Whichever is Higher)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Parental Co-pay Value ({verificationData.coPayStructure.parentalCoPayType.includes('percentage') ? '%' : '₹'})
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={verificationData.coPayStructure.parentalCoPayValue}
                          onChange={(e) => handleCoPayChange("parentalCoPayValue", Math.max(0, parseFloat(e.target.value) || 0))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Location-based Co-pay */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center space-x-3 mb-3">
                  <input
                    type="checkbox"
                    id="locationBasedCoPayApplicable"
                    checked={verificationData.coPayStructure.locationBasedCoPayApplicable}
                    onChange={(e) => handleCoPayChange("locationBasedCoPayApplicable", e.target.checked)}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <label htmlFor="locationBasedCoPayApplicable" className="text-sm font-medium text-blue-900">
                    Location-based Co-pay (Metro/Non-Metro)
                  </label>
                </div>

                {verificationData.coPayStructure.locationBasedCoPayApplicable && (
                  <div className="space-y-4">
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Metro Hospitals</h5>
                      <div className="grid grid-cols-2 gap-3">
                        <select
                          value={verificationData.coPayStructure.metroCoPayType}
                          onChange={(e) => handleCoPayChange("metroCoPayType", e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                        >
                          <option value="percentage_of_admissible">% of Claim Admissible Amount</option>
                          <option value="fixed_amount">Fixed Amount on Admissible Amount</option>
                          <option value="percentage_or_amount_higher">% or Amount (Whichever is Higher)</option>
                        </select>
                        <input
                          type="number"
                          min="0"
                          value={verificationData.coPayStructure.metroCoPayValue}
                          onChange={(e) => handleCoPayChange("metroCoPayValue", Math.max(0, parseFloat(e.target.value) || 0))}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Non-Metro Hospitals</h5>
                      <div className="grid grid-cols-2 gap-3">
                        <select
                          value={verificationData.coPayStructure.nonMetroCoPayType}
                          onChange={(e) => handleCoPayChange("nonMetroCoPayType", e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                        >
                          <option value="percentage_of_admissible">% of Claim Admissible Amount</option>
                          <option value="fixed_amount">Fixed Amount on Admissible Amount</option>
                          <option value="percentage_or_amount_higher">% or Amount (Whichever is Higher)</option>
                        </select>
                        <input
                          type="number"
                          min="0"
                          value={verificationData.coPayStructure.nonMetroCoPayValue}
                          onChange={(e) => handleCoPayChange("nonMetroCoPayValue", Math.max(0, parseFloat(e.target.value) || 0))}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Non-Network Hospital Co-pay */}
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 mt-4">
                <div className="flex items-center space-x-3 mb-3">
                  <input
                    type="checkbox"
                    id="nonNetworkCoPayApplicable"
                    checked={verificationData.coPayStructure.nonNetworkCoPayApplicable}
                    onChange={(e) => handleCoPayChange("nonNetworkCoPayApplicable", e.target.checked)}
                    className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                  />
                  <label htmlFor="nonNetworkCoPayApplicable" className="text-sm font-medium text-yellow-900">
                    Non-Network Hospital Co-pay
                  </label>
                </div>

                {verificationData.coPayStructure.nonNetworkCoPayApplicable && (
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={verificationData.coPayStructure.nonNetworkCoPayType}
                      onChange={(e) => handleCoPayChange("nonNetworkCoPayType", e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-white"
                    >
                      <option value="percentage_of_admissible">% of Claim Admissible Amount</option>
                      <option value="fixed_amount">Fixed Amount on Admissible Amount</option>
                      <option value="percentage_or_amount_higher">% or Amount (Whichever is Higher)</option>
                    </select>
                    <input
                      type="number"
                      min="0"
                      value={verificationData.coPayStructure.nonNetworkCoPayValue}
                      onChange={(e) => handleCoPayChange("nonNetworkCoPayValue", Math.max(0, parseFloat(e.target.value) || 0))}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder="0"
                    />
                  </div>
                )}
              </div>

              {/* Voluntary/Mandatory Co-pay */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h5 className="text-sm font-medium text-green-900 mb-3">Voluntary/Mandatory Co-pay</h5>

                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="voluntaryCoPayApplicable"
                      checked={verificationData.coPayStructure.voluntaryCoPayApplicable}
                      onChange={(e) => handleCoPayChange("voluntaryCoPayApplicable", e.target.checked)}
                      className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                    />
                    <label htmlFor="voluntaryCoPayApplicable" className="text-sm font-medium text-gray-700">
                      Voluntary Co-pay
                    </label>
                  </div>

                  {verificationData.coPayStructure.voluntaryCoPayApplicable && (
                    <div className="grid grid-cols-2 gap-3 ml-6">
                      <select
                        value={verificationData.coPayStructure.voluntaryCoPayType}
                        onChange={(e) => handleCoPayChange("voluntaryCoPayType", e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                      >
                        <option value="percentage_of_admissible">% of Claim Admissible Amount</option>
                        <option value="fixed_amount">Fixed Amount on Admissible Amount</option>
                        <option value="percentage_or_amount_higher">% or Amount (Whichever is Higher)</option>
                      </select>
                      <input
                        type="number"
                        min="0"
                        value={verificationData.coPayStructure.voluntaryCoPayValue}
                        onChange={(e) => handleCoPayChange("voluntaryCoPayValue", Math.max(0, parseFloat(e.target.value) || 0))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="0"
                      />
                    </div>
                  )}

                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="mandatoryCoPayApplicable"
                      checked={verificationData.coPayStructure.mandatoryCoPayApplicable}
                      onChange={(e) => handleCoPayChange("mandatoryCoPayApplicable", e.target.checked)}
                      className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                    />
                    <label htmlFor="mandatoryCoPayApplicable" className="text-sm font-medium text-gray-700">
                      Mandatory Co-pay
                    </label>
                  </div>

                  {verificationData.coPayStructure.mandatoryCoPayApplicable && (
                    <div className="grid grid-cols-2 gap-3 ml-6">
                      <select
                        value={verificationData.coPayStructure.mandatoryCoPayType}
                        onChange={(e) => handleCoPayChange("mandatoryCoPayType", e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                      >
                        <option value="percentage_of_admissible">% of Claim Admissible Amount</option>
                        <option value="fixed_amount">Fixed Amount on Admissible Amount</option>
                        <option value="percentage_or_amount_higher">% or Amount (Whichever is Higher)</option>
                      </select>
                      <input
                        type="number"
                        min="0"
                        value={verificationData.coPayStructure.mandatoryCoPayValue}
                        onChange={(e) => handleCoPayChange("mandatoryCoPayValue", Math.max(0, parseFloat(e.target.value) || 0))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="0"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Non-intimation Co-pay (only if intimation is mandatory) */}
              {verificationData.intimationDetails.intimationMandatory && (
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <div className="flex items-center space-x-3 mb-3">
                    <input
                      type="checkbox"
                      id="nonIntimationCoPayApplicable"
                      checked={verificationData.coPayStructure.nonIntimationCoPayApplicable}
                      onChange={(e) => handleCoPayChange("nonIntimationCoPayApplicable", e.target.checked)}
                      className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                    />
                    <label htmlFor="nonIntimationCoPayApplicable" className="text-sm font-medium text-red-900">
                      Co-pay for Non-intimation (When intimation is mandatory)
                    </label>
                  </div>

                  {verificationData.coPayStructure.nonIntimationCoPayApplicable && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Non-intimation Co-pay Type
                        </label>
                        <select
                          value={verificationData.coPayStructure.nonIntimationCoPayType}
                          onChange={(e) => handleCoPayChange("nonIntimationCoPayType", e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                        >
                          <option value="percentage_of_admissible">% of Claim Admissible Amount</option>
                          <option value="fixed_amount">Fixed Amount on Admissible Amount</option>
                          <option value="percentage_or_amount_higher">% or Amount (Whichever is Higher)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Non-intimation Co-pay Value ({verificationData.coPayStructure.nonIntimationCoPayType.includes('percentage') ? '%' : '₹'})
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={verificationData.coPayStructure.nonIntimationCoPayValue}
                          onChange={(e) => handleCoPayChange("nonIntimationCoPayValue", Math.max(0, parseFloat(e.target.value) || 0))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderDeductiblesAndSublimits = () => (
    <div className="space-y-6">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Calculator className="h-5 w-5 text-orange-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-orange-900">
              Deductibles & Sublimits
            </h4>
            <p className="text-sm text-orange-700 mt-1">
              Configure deductibles and sublimits as per policy terms.
            </p>
          </div>
        </div>
      </div>

      {/* Deductibles & Sublimits Mandatory Check */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="flex items-start space-x-3">
          <div className="flex items-center space-x-3">
            <input
              type="radio"
              id="deductiblesMandatoryYes"
              name="deductiblesMandatory"
              checked={verificationData.deductiblesAndSublimits.deductiblesMandatory === true}
              onChange={() => handleDeductibleChange("deductiblesMandatory", true)}
              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300"
            />
            <label htmlFor="deductiblesMandatoryYes" className="text-sm font-medium text-blue-900">
              Yes, Deductibles & Sublimits Apply
            </label>
          </div>
          <div className="flex items-center space-x-3 ml-6">
            <input
              type="radio"
              id="deductiblesMandatoryNo"
              name="deductiblesMandatory"
              checked={verificationData.deductiblesAndSublimits.deductiblesMandatory === false}
              onChange={() => handleDeductibleChange("deductiblesMandatory", false)}
              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300"
            />
            <label htmlFor="deductiblesMandatoryNo" className="text-sm font-medium text-blue-900">
              No Deductibles or Sublimits
            </label>
          </div>
        </div>
        <p className="text-sm text-blue-700 mt-2">
          {verificationData.deductiblesAndSublimits.deductiblesMandatory ?
            "Configure the applicable deductibles and sublimits below:" :
            "No deductibles or sublimits will be applied to this policy."
          }
        </p>
      </div>

      {/* Show deductibles & sublimits options only if mandatory */}
      {verificationData.deductiblesAndSublimits.deductiblesMandatory && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Deductibles */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900">Deductibles</h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Compulsory Deductible (₹)
              </label>
              <input
                type="number"
                min="0"
                value={verificationData.deductiblesAndSublimits.compulsoryDeductible}
                onChange={(e) => handleDeductibleChange("compulsoryDeductible", Math.max(0, parseFloat(e.target.value) || 0))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pre/Post Natal Limit (₹)
              </label>
              <input
                type="number"
                min="0"
                value={verificationData.deductiblesAndSublimits.prePostNatalLimit}
                onChange={(e) => handleDeductibleChange("prePostNatalLimit", Math.max(0, parseFloat(e.target.value) || 0))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                placeholder="0"
              />
            </div>
          </div>

          {/* Sublimits */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-semibold text-gray-900">Ailment/Procedure Sublimits</h4>
              <button
                onClick={addSublimit}
                className="flex items-center space-x-2 px-3 py-1 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm"
              >
                <Plus className="h-4 w-4" />
                <span>Add Sublimit</span>
              </button>
            </div>

            {verificationData.deductiblesAndSublimits.ailmentProcedureSublimits.length > 0 ? (
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {verificationData.deductiblesAndSublimits.ailmentProcedureSublimits.map((sublimit, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-3 p-3 bg-gray-50 border border-gray-200 rounded-lg"
                  >
                    <input
                      type="text"
                      placeholder="Ailment/Procedure"
                      value={sublimit.ailmentProcedure}
                      onChange={(e) => updateSublimit(index, "ailmentProcedure", e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                    <input
                      type="number"
                      min="0"
                      placeholder="Sublimit Amount"
                      value={sublimit.sublimitAmount}
                      onChange={(e) => updateSublimit(index, "sublimitAmount", Math.max(0, parseFloat(e.target.value) || 0))}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      onClick={() => removeSublimit(index)}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No sublimits configured</p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderIntimationAndClaims = () => (
    <div className="space-y-6">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Bell className="h-5 w-5 text-purple-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-purple-900">
              Intimation & Claim History
            </h4>
            <p className="text-sm text-purple-700 mt-1">
              Verify intimation details and review current year claim history.
            </p>
          </div>
        </div>
      </div>

      {/* Important Notification for Intimation */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-red-900">Important Notice</h4>
            <p className="text-sm text-red-700 mt-1">
              <strong>Important:</strong> Claim Intimation is typically required for all claims. Please verify if intimation is mandatory for this policy and ensure it's completed before processing.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Intimation Details */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900">Intimation Details</h4>

          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <div className="flex items-center space-x-3 mb-3">
              <input
                type="checkbox"
                id="intimationDone"
                checked={verificationData.intimationDetails.intimationDone}
                onChange={(e) => handleIntimationChange("intimationDone", e.target.checked)}
                className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <label htmlFor="intimationDone" className="text-sm font-medium text-yellow-900">
                Is Intimation Done?
              </label>
            </div>

            {verificationData.intimationDetails.intimationDone && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Intimation ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={verificationData.intimationDetails.intimationId}
                    onChange={(e) => handleIntimationChange("intimationId", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Enter intimation ID"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Intimation Source <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={verificationData.intimationDetails.intimationSource}
                    onChange={(e) => handleIntimationChange("intimationSource", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                    required
                  >
                    <option value="">Select Source</option>
                    <option value="Phone">Phone</option>
                    <option value="Online">Online Portal</option>
                    <option value="Mobile App">Mobile App</option>
                    <option value="Hospital">Hospital</option>
                    <option value="Email">Email</option>
                    <option value="SMS">SMS</option>
                  </select>
                </div>
              </div>
            )}

            {!verificationData.intimationDetails.intimationDone && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
                  <p className="text-sm text-red-700">
                    <strong>Important:</strong> Intimation is typically required for cashless claims.
                    Please verify if intimation is mandatory for this policy and ensure it's completed before processing.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Claim History */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold text-gray-900">Claim History (Current Year)</h4>
            <button
              onClick={addPreviousClaim}
              className="flex items-center space-x-2 px-3 py-1 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm"
            >
              <Plus className="h-4 w-4" />
              <span>Add Claim</span>
            </button>
          </div>

          {verificationData.claimHistory.previousClaimsCurrentYear.length > 0 ? (
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {verificationData.claimHistory.previousClaimsCurrentYear.map((claim, index) => (
                <div
                  key={index}
                  className="p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2"
                >
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      placeholder="Claim Number"
                      value={claim.claimNumber}
                      onChange={(e) => updatePreviousClaim(index, "claimNumber", e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                    <button
                      onClick={() => removePreviousClaim(index)}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={claim.claimDate}
                      onChange={(e) => updatePreviousClaim(index, "claimDate", e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                    <input
                      type="number"
                      min="0"
                      placeholder="Amount"
                      value={claim.claimAmount}
                      onChange={(e) => updatePreviousClaim(index, "claimAmount", Math.max(0, parseFloat(e.target.value) || 0))}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                  <select
                    value={claim.status}
                    onChange={(e) => updatePreviousClaim(index, "status", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="Settled">Settled</option>
                    <option value="Pending">Pending</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No previous claims this year</p>
          )}

          {verificationData.claimHistory.totalClaimedCurrentYear > 0 && (
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-blue-900">Total Claimed This Year:</span>
                <span className="text-lg font-bold text-blue-600">
                  {formatAmount(verificationData.claimHistory.totalClaimedCurrentYear)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderPolicyAnalysis = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900">
              Policy Eligibility Analysis
            </h4>
            <p className="text-sm text-blue-700 mt-1">
              Verify policy eligibility and coverage conditions based on the provided details.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Policy Validity Checks */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900">Policy Validity</h4>

          {[
            { key: "policyValid", label: "Policy is Valid & Active" },
            { key: "coverageActive", label: "Coverage is Currently Active" },
            { key: "beneficiaryEligible", label: "Patient is Eligible Beneficiary" },
            { key: "treatmentCovered", label: "Treatment is Covered Under Policy" },
          ].map(({ key, label }) => (
            <div
              key={key}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <span className="text-sm font-medium text-gray-700">{label}</span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleAnalysisChange(key, true)}
                  className={`p-1 rounded ${verificationData.policyAnalysis[key] === true
                    ? "bg-green-600 text-white"
                    : "bg-gray-200 text-gray-600 hover:bg-green-100"
                    }`}
                >
                  <CheckCircle className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleAnalysisChange(key, false)}
                  className={`p-1 rounded ${verificationData.policyAnalysis[key] === false
                    ? "bg-red-600 text-white"
                    : "bg-gray-200 text-gray-600 hover:bg-red-100"
                    }`}
                >
                  <XCircle className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Authorization & Limits */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900">Authorization & Limits</h4>

          {[
            { key: "preAuthRequired", label: "Pre-Authorization Required" },
            { key: "preAuthObtained", label: "Pre-Authorization Obtained" },
            { key: "claimLimitAvailable", label: "Claim Limit Available" },
            { key: "waitingPeriodCompleted", label: "Waiting Period Completed" },
          ].map(({ key, label }) => (
            <div
              key={key}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <span className="text-sm font-medium text-gray-700">{label}</span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleAnalysisChange(key, true)}
                  className={`p-1 rounded ${verificationData.policyAnalysis[key] === true
                    ? "bg-green-600 text-white"
                    : "bg-gray-200 text-gray-600 hover:bg-green-100"
                    }`}
                >
                  <CheckCircle className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleAnalysisChange(key, false)}
                  className={`p-1 rounded ${verificationData.policyAnalysis[key] === false
                    ? "bg-red-600 text-white"
                    : "bg-gray-200 text-gray-600 hover:bg-red-100"
                    }`}
                >
                  <XCircle className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Exclusions Applied */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900">Exclusions Applied</h4>
          <button
            onClick={addExclusion}
            className="flex items-center space-x-2 px-3 py-1 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>Add Exclusion</span>
          </button>
        </div>

        {verificationData.exclusionsApplied.length > 0 ? (
          <div className="space-y-3">
            {verificationData.exclusionsApplied.map((exclusion, index) => (
              <div
                key={index}
                className="flex items-center space-x-3 p-3 bg-red-50 border border-red-200 rounded-lg"
              >
                <input
                  type="text"
                  placeholder="Exclusion reason"
                  value={exclusion.reason}
                  onChange={(e) => updateExclusion(index, "reason", e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Amount"
                  value={exclusion.amount}
                  onChange={(e) => updateExclusion(index, "amount", Math.max(0, parseFloat(e.target.value) || 0))}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                  onClick={() => removeExclusion(index)}
                  className="text-red-600 hover:text-red-800 p-1"
                >
                  <Minus className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No exclusions applied</p>
        )}
      </div>

      {/* Policy Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Policy Summary & Coverage Breakdown</h4>

        {/* Coverage Breakdown */}
        <div className="mb-6">
          <h5 className="text-sm font-medium text-gray-700 mb-3">Coverage Breakdown</h5>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white rounded-lg p-3 border">
              <label className="text-xs font-medium text-gray-500">Base Sum Insured</label>
              <p className="text-lg font-semibold text-blue-600">
                {formatAmount(verificationData.coverageBreakdown.baseCoverage)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-3 border">
              <label className="text-xs font-medium text-gray-500">Cumulative Bonus</label>
              <p className="text-lg font-semibold text-green-600">
                {formatAmount(verificationData.coverageBreakdown.cumulativeBonus)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-3 border">
              <label className="text-xs font-medium text-gray-500">Top-up Coverage</label>
              <p className="text-lg font-semibold text-purple-600">
                {formatAmount(verificationData.coverageBreakdown.topUpCoverage)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-3 border">
              <label className="text-xs font-medium text-gray-500">Total Available</label>
              <p className="text-lg font-semibold text-gray-900">
                {formatAmount(verificationData.coverageBreakdown.totalAvailable)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-3 border">
              <label className="text-xs font-medium text-gray-500">Already Utilized</label>
              <p className="text-lg font-semibold text-red-600">
                {formatAmount(verificationData.coverageBreakdown.utilized)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-200 bg-green-50">
              <label className="text-xs font-medium text-green-700">Balance Available</label>
              <p className="text-lg font-bold text-green-600">
                {formatAmount(verificationData.coverageBreakdown.balance)}
              </p>
            </div>
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">Approved Coverage</label>
            <p className="text-lg font-bold text-green-600">
              {formatAmount(verificationData.approvedCoverageAmount)}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Claim Amount</label>
            <p className="text-lg font-semibold text-gray-900">
              {formatAmount(application?.medical?.estimatedCost)}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Policy Status</label>
            <p className={`text-sm font-medium ${verificationData.policyDetails.expiryDate &&
              new Date(verificationData.policyDetails.expiryDate) > new Date()
              ? "text-green-600" : "text-red-600"
              }`}>
              {verificationData.policyDetails.expiryDate &&
                new Date(verificationData.policyDetails.expiryDate) > new Date()
                ? "Active" : "Expired"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDispatchAddress = () => {
    const da = verificationData.dispatchAddress || {
      recipientName: "",
      contactNumber: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      pincode: "",
    };
    const dt = String((verificationData.dispatchAddress || {}).dispatchType || "offline").toLowerCase();
    const rt = String((verificationData.dispatchAddress || {}).recipientType || "insurance").toLowerCase();
    return (
    <div className="space-y-6">
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <MapPin className="h-5 w-5 text-indigo-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-indigo-900">
              Document Dispatch
            </h4>
            <p className="text-sm text-indigo-700 mt-1">
              Choose dispatch type. Address fields are hidden for Online.
            </p>
          </div>
        </div>
      </div>

      {/* Dispatch Type Selector */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Dispatch Type
          </label>
          <select
            value={dt}
            onChange={(e) => handleDispatchAddressChange("dispatchType", e.target.value)}
            disabled={isApproved}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
              isApproved ? 'bg-gray-100 cursor-not-allowed' : ''
            }`}
          >
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="both">Both</option>
          </select>
        </div>
      </div>

      {/* Recipient Type Radio Buttons */}
      {dt !== 'online' && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Recipient Type
          </label>
          <div className="flex space-x-6">
            <label className="flex items-center">
              <input
                type="radio"
                name="recipientType"
                value="insurance"
                checked={rt === 'insurance'}
                onChange={(e) => {
                  handleDispatchAddressChange("recipientType", e.target.value);
                  // Auto-fill Insurance Company name from application data
                  const insurer =
                    application?.policy?.insuranceCompany ||
                    verificationData?.policyDetails?.insuranceCompany ||
                    application?.insuranceCompany ||
                    '';
                  handleDispatchAddressChange("recipientName", insurer);
                }}
                disabled={isApproved}
                className="mr-2 text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">Insurance Company</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="recipientType"
                value="tpa"
                checked={rt === 'tpa'}
                onChange={(e) => {
                  handleDispatchAddressChange("recipientType", e.target.value);
                  // Auto-fill TPA name from application data (robust fallbacks)
                  const tpaName =
                    application?.policy?.tpaName ||
                    verificationData?.policyDetails?.tpaName ||
                    application?.tpaName ||
                    application?.tpa ||
                    "";
                  handleDispatchAddressChange("recipientName", tpaName);
                }}
                disabled={isApproved}
                className="mr-2 text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">TPA</span>
            </label>
          </div>
        </div>
      </div>
      )}

      {dt !== 'online' && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipient Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={da.recipientName}
              onChange={(e) => handleDispatchAddressChange("recipientName", e.target.value)}
              disabled={isApproved}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                isApproved ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              placeholder="Full name / Department"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address Line 1 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={da.addressLine1}
              onChange={(e) => handleDispatchAddressChange("addressLine1", e.target.value)}
              disabled={isApproved}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                isApproved ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              placeholder="House/Flat, Building, Street"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address Line 2
            </label>
            <input
              type="text"
              value={da.addressLine2}
              onChange={(e) => handleDispatchAddressChange("addressLine2", e.target.value)}
              disabled={isApproved}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                isApproved ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              placeholder="Area, Landmark (optional)"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={da.city}
                onChange={(e) => handleDispatchAddressChange("city", e.target.value)}
                disabled={isApproved}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                  isApproved ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                placeholder="City"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={da.state}
                onChange={(e) => handleDispatchAddressChange("state", e.target.value)}
                disabled={isApproved}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                  isApproved ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                placeholder="State"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pincode <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={da.pincode}
                onChange={(e) => handleDispatchAddressChange("pincode", e.target.value.replace(/\D/g, ""))}
                disabled={isApproved}
                maxLength={6}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                  isApproved ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                placeholder="6-digit pincode"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Number <span className="text-gray-400 text-xs font-normal">(optional)</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="tel"
                  pattern="[0-9]*"
                  value={da.contactNumber}
                  onChange={(e) => handleDispatchAddressChange("contactNumber", e.target.value.replace(/\D/g, ""))}
                  disabled={isApproved}
                  maxLength={10}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 pr-10 ${
                    isApproved ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  placeholder="10-digit mobile"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
                  <Phone className="h-4 w-4" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
  };

  // Q&A Question management
  const handleAddQnaQuestion = () => {
    const newQuestion = verificationData.clarificationDetails.newQuestion;
    if (!newQuestion?.question?.trim()) return;

    const questionWithId = {
      ...newQuestion,
      id: `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };

    setVerificationData((prev) => ({
      ...prev,
      clarificationDetails: {
        ...prev.clarificationDetails,
        qnaQuestions: [...(prev.clarificationDetails.qnaQuestions || []), questionWithId],
        newQuestion: {
          question: "",
          required: false,
          answerType: "text",
          description: "",
        },
      },
    }));
  };

  const removeQnaQuestion = (questionId) => {
    setVerificationData((prev) => ({
      ...prev,
      clarificationDetails: {
        ...prev.clarificationDetails,
        qnaQuestions: (prev.clarificationDetails.qnaQuestions || []).filter((q) => q.id !== questionId),
      },
    }));
  };

  const renderClarificationRequest = () => (
    <div className="space-y-6">
      {/* Validation Error Alert */}
      {validationErrors.clarification && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-red-900">
                Required Information Missing
              </h4>
              <p className="text-sm text-red-700 mt-1">
                {validationErrors.clarification}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-yellow-900">
              Request Additional Information - Q&A Form
            </h4>
            <p className="text-sm text-yellow-700 mt-1">
              Create multiple questions to request specific information or documents from the hospital. Each question can require text answers, file uploads, or both.
            </p>
          </div>
        </div>
      </div>

      {/* Existing Questions */}
      {verificationData.clarificationDetails.qnaQuestions && verificationData.clarificationDetails.qnaQuestions.length > 0 && (
        <div className="space-y-4">
          <h5 className="text-sm font-medium text-gray-700">
            Questions ({verificationData.clarificationDetails.qnaQuestions.length})
          </h5>
          <div className="space-y-3">
            {verificationData.clarificationDetails.qnaQuestions.map((q, index) => (
              <div key={q.id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-sm font-semibold text-purple-600">
                        Q{index + 1}:
                      </span>
                      {q.required && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                          Required
                        </span>
                      )}
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {q.answerType === 'text' ? 'Text' :
                          q.answerType === 'file' ? 'File' : 'Text + File'}
                      </span>
                    </div>
                    <p className="text-gray-900 font-medium text-sm mb-1">
                      {q.question}
                    </p>
                    {q.description && (
                      <p className="text-sm text-gray-600">
                        {q.description}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => removeQnaQuestion(q.id)}
                    className="text-red-600 hover:text-red-800 p-1 ml-3"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add New Question */}
      <div className="border-t border-gray-200 pt-6">
        <h5 className="text-sm font-medium text-gray-700 mb-4">
          Add New Question
        </h5>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Question <span className="text-red-500">*</span>
            </label>
            <textarea
              value={verificationData.clarificationDetails.newQuestion?.question || ""}
              onChange={(e) => {
                setVerificationData((prev) => ({
                  ...prev,
                  clarificationDetails: {
                    ...prev.clarificationDetails,
                    newQuestion: {
                      ...prev.clarificationDetails.newQuestion,
                      question: e.target.value,
                    },
                  },
                }));
                // Clear validation error when user starts typing
                if (validationErrors.clarification) {
                  setValidationErrors(prev => ({ ...prev, clarification: null }));
                }
              }}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Enter your question..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <input
              type="text"
              value={verificationData.clarificationDetails.newQuestion?.description || ""}
              onChange={(e) =>
                setVerificationData((prev) => ({
                  ...prev,
                  clarificationDetails: {
                    ...prev.clarificationDetails,
                    newQuestion: {
                      ...prev.clarificationDetails.newQuestion,
                      description: e.target.value,
                    },
                  },
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Add additional context or instructions..."
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Answer Type
                </label>
                <select
                  value={verificationData.clarificationDetails.newQuestion?.answerType || "text"}
                  onChange={(e) =>
                    setVerificationData((prev) => ({
                      ...prev,
                      clarificationDetails: {
                        ...prev.clarificationDetails,
                        newQuestion: {
                          ...prev.clarificationDetails.newQuestion,
                          answerType: e.target.value,
                        },
                      },
                    }))
                  }
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="text">Text Answer</option>
                  <option value="file">File Upload</option>
                  <option value="both">Text + File</option>
                </select>
              </div>

              <div className="pt-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={verificationData.clarificationDetails.newQuestion?.required || false}
                    onChange={(e) =>
                      setVerificationData((prev) => ({
                        ...prev,
                        clarificationDetails: {
                          ...prev.clarificationDetails,
                          newQuestion: {
                            ...prev.clarificationDetails.newQuestion,
                            required: e.target.checked,
                          },
                        },
                      }))
                    }
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Required</span>
                </label>
              </div>
            </div>

            <button
              onClick={handleAddQnaQuestion}
              disabled={!verificationData.clarificationDetails.newQuestion?.question?.trim()}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add Question</span>
            </button>
          </div>
        </div>
      </div>

      {/* Priority and Overall Message */}
      <div className="border-t border-gray-200 pt-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Overall Message (Optional)
          </label>
          <textarea
            value={verificationData.clarificationDetails.message}
            onChange={(e) => {
              setVerificationData((prev) => ({
                ...prev,
                clarificationDetails: {
                  ...prev.clarificationDetails,
                  message: e.target.value,
                },
              }));
            }}
            rows="4"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Add any general instructions or context for the Q&A form..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Priority
          </label>
          <select
            value={verificationData.clarificationDetails.priority}
            onChange={(e) =>
              setVerificationData((prev) => ({
                ...prev,
                clarificationDetails: {
                  ...prev.clarificationDetails,
                  priority: e.target.value,
                },
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderComments = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Verification Comments
        </label>
        <textarea
          value={verificationData.comments}
          onChange={(e) =>
            setVerificationData((prev) => ({
              ...prev,
              comments: e.target.value,
            }))
          }
          rows="6"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          placeholder="Provide detailed comments about your policy verification decision..."
        />
        <p className="text-sm text-gray-500 mt-1">
          Comments are required for rejected applications and recommended for all decisions.
        </p>
      </div>

      {/* Review Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Review Summary</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">Application ID</label>
            <p className="text-sm text-gray-900">{application?.applicationId}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Patient</label>
            <p className="text-sm text-gray-900">{application?.patient?.name}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Policy Number</label>
            <p className="text-sm text-gray-900">{verificationData.policyDetails.policyNumber || "Not provided"}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Claim Amount</label>
            <p className="text-sm text-gray-900">{formatAmount(application?.medical?.estimatedCost)}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Available Coverage</label>
            <p className="text-lg font-bold text-green-600">
              {formatAmount(verificationData.policyDetails.balanceSumInsured)}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Review Time</label>
            <p className="text-sm text-gray-900">
              {Math.round((new Date() - reviewStartTime) / (1000 * 60))} minutes
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Reviewer</label>
            <p className="text-sm text-gray-900">{user?.email}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Policy Status</label>
            <p className={`text-sm font-medium ${verificationData.policyDetails.expiryDate &&
              new Date(verificationData.policyDetails.expiryDate) > new Date()
              ? "text-green-600" : "text-red-600"
              }`}>
              {verificationData.policyDetails.expiryDate &&
                new Date(verificationData.policyDetails.expiryDate) > new Date()
                ? "Active" : "Expired"}
            </p>
          </div>
        </div>

        {/* Eligibility Confirmation */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="eligibilityConfirmed"
              checked={verificationData.eligibilityConfirmed}
              onChange={(e) =>
                setVerificationData((prev) => ({
                  ...prev,
                  eligibilityConfirmed: e.target.checked,
                }))
              }
              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
            />
            <label htmlFor="eligibilityConfirmed" className="text-sm font-medium text-gray-700">
              I confirm that I have verified all policy details and eligibility criteria
            </label>
          </div>
        </div>

          {/* Dispatch Address Preview */}
          <div className="mt-4 pt-4 border-t border-gray-200">
        <h5 className="text-sm font-medium text-gray-700 mb-2">Dispatch Address</h5>
        {(verificationData.dispatchAddress && verificationData.dispatchAddress.recipientName) ? (
              <div className="text-sm text-gray-700">
          <p className="font-medium">{verificationData.dispatchAddress.recipientName} {verificationData.dispatchAddress.contactNumber ? `• ${verificationData.dispatchAddress.contactNumber}` : ""}</p>
          <p>{verificationData.dispatchAddress.addressLine1 || ""}</p>
          {verificationData.dispatchAddress.addressLine2 && <p>{verificationData.dispatchAddress.addressLine2}</p>}
                <p>
                  {[verificationData.dispatchAddress.city, verificationData.dispatchAddress.state, verificationData.dispatchAddress.pincode]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No dispatch address provided yet.</p>
            )}
          </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Enhanced Header */}
      <div className="px-6 py-5 bg-gradient-to-r from-teal-50 to-blue-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-teal-100 p-2 rounded-lg">
              <Shield className="h-6 w-6 text-teal-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                Policy Verification
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Complete verification for <span className="font-medium text-gray-800">{application?.patient?.name}</span>
                {application?.applicationId && (
                  <span className="text-gray-500"> • ID: {application.applicationId}</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              {!isApproved && (
                <div className="flex items-center space-x-2 text-sm text-green-600 mb-1">
                  <div className="h-2 w-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span>Auto-saving every 10 seconds</span>
                </div>
              )}
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Clock className="h-4 w-4" />
                <span>Review Time: {Math.round((new Date() - reviewStartTime) / (1000 * 60))} min</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Reviewer: {user?.email}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content - All Sections */}
      <div className="p-6 space-y-8">
        {/* Edit override banner when KYP challenged and reassigned to Policy */}
        {isChallengedForPolicy && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="bg-yellow-100 p-1 rounded-full">
                <AlertTriangle className="h-4 w-4 text-yellow-700" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-yellow-800">Edit override active due to hospital challenge</h4>
                <p className="text-xs text-yellow-700 mt-1">
                  Hospital challenged the KYP and the doctor reassigned this case to Policy. Editing is enabled here until the revision cycle is completed.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Policy Details Section */}
        <div>
          {renderPolicyDetails()}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200"></div>

        {/* Room & Co-pay Section */}
        <div>
          {renderRoomAndCoPay()}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200"></div>

        {/* Deductibles & Sublimits Section */}
        <div>
          {renderDeductiblesAndSublimits()}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200"></div>

        {/* Intimation & Claims Section */}
        <div>
          {renderIntimationAndClaims()}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200"></div>

        {/* Policy Analysis Section */}
        <div>
          {renderPolicyAnalysis()}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200"></div>

        {/* Document Dispatch Address Section */}
        <div>
          {renderDispatchAddress()}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200"></div>

        {/* Clarification Request Section */}
        {/* <div>
          {renderClarificationRequest()}
        </div> */}

        {/* Divider */}
        <div className="border-t border-gray-200"></div>

        {/* Comments & Summary Section */}
        <div>
          {renderComments()}
        </div>
      </div>

      {/* Enhanced Action Buttons */}
      <div className="px-6 py-5 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        {/* Show approval status message */}
        {isApproved && (
          <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="bg-green-100 p-1 rounded-full">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-green-800">
                  Application Already Approved
                </h4>
                <p className="text-sm text-green-700 mt-1">
                  This policy verification has been completed and approved. No further changes can be made to the form.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* DEBUG: Show current approval status */}
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-xs text-yellow-800">
            <strong>DEBUG - isApproved:</strong> {String(isApproved)} | 
            <strong> Status:</strong> {application?.status} | 
            <strong> Workflow Status:</strong> {application?.workflow?.policyVerification?.status}
          </p>
        </div>
        
        {/* Warning for pending clarifications */}
        {hasPendingClarification && !isApproved && (
          <div className="mb-4 p-4 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="bg-orange-100 p-1 rounded-full">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-orange-800">
                  Clarification Required
                </h4>
                <p className="text-sm text-orange-700 mt-1">
                  This application has pending clarification requests. Please resolve all clarifications through the discussion panel before approval.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium flex items-center space-x-2"
          >
            <span>{isApproved ? 'Close' : 'Cancel'}</span>
          </button>

          {/* Only show action buttons if not approved */}
          {!isApproved && (
            <div className="flex items-center space-x-3">
              {/* Optional Draft Save Status Indicator */}
              <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium ${
                savedAsDraft 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : 'bg-blue-100 text-blue-800 border border-blue-200'
              }`}>
                {savedAsDraft ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    <span>Saved as Draft</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Draft Available</span>
                  </>
                )}
              </div>

            <button
              onClick={handleManualSave}
              disabled={isApproved}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-200 font-medium shadow-sm ${
                isApproved 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : savedAsDraft
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-600 text-white hover:bg-gray-700'
              }`}
              title={isApproved ? 'Cannot save - already approved' : 'Save current progress'}
            >
              <Save className="h-4 w-4" />
              <span>{savedAsDraft ? 'Update Draft' : 'Save Draft'}</span>
            </button>

            <button
              onClick={() => handleSubmit("pending_info")}
              disabled={loading || isApproved}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm ${
                isApproved 
                  ? 'bg-gray-300 text-gray-500' 
                  : 'bg-yellow-600 text-white hover:bg-yellow-700'
              }`}
              title={isApproved ? 'Cannot request info - already approved' : 'Request information'}
            >
              <AlertTriangle className="h-4 w-4" />
              <span>{loading ? "Processing..." : "Request Info"}</span>
            </button>

            <button
              onClick={() => handleSubmit("rejected")}
              disabled={loading || isApproved}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm ${
                isApproved 
                  ? 'bg-gray-300 text-gray-500' 
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
              title={isApproved ? 'Cannot reject - already approved' : 'Reject application'}
            >
              <XCircle className="h-4 w-4" />
              <span>{loading ? "Processing..." : "Reject"}</span>
            </button>

            <button
              onClick={() => handleSubmit("approved")}
              disabled={loading || !verificationData.eligibilityConfirmed || hasPendingClarification || isApproved}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm ${
                isApproved 
                  ? "bg-green-200 text-green-800 border-2 border-green-300"
                  : hasPendingClarification
                    ? "bg-gray-400 text-white"
                    : "bg-green-600 text-white hover:bg-green-700"
              }`}
              title={
                isApproved 
                  ? "Already approved" 
                  : hasPendingClarification 
                    ? "Cannot approve while clarification is pending" 
                    : "Approve policy"
              }
            >
              {isApproved ? (
                <CheckCircle className="h-4 w-4" />
              ) : hasPendingClarification ? (
                <AlertTriangle className="h-4 w-4" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              <span>
                {isApproved 
                  ? "Already Approved" 
                  : loading 
                    ? "Processing..." 
                    : hasPendingClarification 
                      ? "Pending Info" 
                      : "Approve"
                }
              </span>
            </button>
          </div>
          )}
        </div>

        {/* Enhanced Note about coverage calculation - only show when not approved */}
        {!isApproved && (
          <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="bg-blue-100 p-1 rounded-full">
                <Info className="h-4 w-4 text-blue-600" />
              </div>
              <div className="text-sm text-blue-700">
                <p className="font-medium">Coverage Calculation</p>
                <p className="mt-1">Final coverage calculations and disbursement amounts will be determined during the discharge review stage after policy approval.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Rejection Dialog */}
      {showRejectionDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <XCircle className="h-6 w-6 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900">Document Rejection Reason</h3>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Please provide a detailed reason for rejection:
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter the specific reasons for rejecting this policy application..."
                rows={4}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                maxLength={500}
              />
              <div className="text-right text-xs text-gray-500 mt-1">
                {rejectionReason.length}/500 characters
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRejectionDialog(false);
                  setRejectionReason('');
                }}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectionSubmit}
                disabled={!rejectionReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyVerificationForm;